import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Palette, Palette as PaletteIcon, Library, Briefcase, UploadCloud, Loader2, FileText, Type, ShoppingBag, Users, User, Image as ImageIcon, Sparkles, Mic2, Zap, UserCircle, CheckCircle, ArrowRight, ArrowLeft, ThumbsUp, Mail, Info, Trash2, Layers, Settings, ChevronDown, ChevronUp, Scissors, Shirt, Monitor, X, Check, Eye, Plus, Camera, LayoutTemplate, ZoomIn } from 'lucide-react';
import { PRODUCT_CATEGORIES_OPTIMIZED } from '../data/productCategoriesOptimized';
import { FABRIC_TEXTURES } from '../data/fabricTextures';
import {
  WEAVE_OR_KNIT_OPTIONS,
  OPACITY_OPTIONS,
  STRETCH_OPTIONS,
  GSM_WEIGHT_OPTIONS,
  FABRIC_VISUAL_TYPE_OPTIONS
} from '../data/fabricAnalysisOptions';
import { DEFAULT_FABRIC_PRESETS } from '../data/defaultFabricPresets';
import { createFabricSwatchImageFile } from '../utils/createFabricSwatchImageFile';
import { uploadImageToS3, compressImage, createThumbnailImageFile } from '../utils/s3Upload';
import { getInspirationalDesigns, createInspirationalDesign } from '../api/inspirationalDesign.api';
import { extractColorVariants, getProducts } from '../api/product.api';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';
import generateKitBackground from '../assets/generate-kit-background.svg';
import {
  KIDS_BOY_TODDLER_PREVIEW,
  KIDS_GIRL_TODDLER_PREVIEW,
  MEN_CLOTHING_CATEGORY_PREVIEW,
  KIDS_BOY_CATEGORY_PREVIEW,
  WOMEN_CLOTHING_CATEGORY_PREVIEW,
  encodeAssetPath,
} from '../data/garmentInspirationImages';
import { hydrateFabricTemplate } from './fabricTemplates/templates';
import { useFabricTemplates } from './fabricTemplates/useFabricTemplates';
import AddFabricTemplateModal from './fabricTemplates/AddFabricTemplateModal';
import FabricTemplateSetupFields from './fabricTemplates/FabricTemplateSetupFields';
import FabricTemplatePropertiesEditor, {
  snapshotTemplateFabricProperties,
  templatePropertiesToGenerationOverrides,
} from './fabricTemplates/FabricTemplatePropertiesEditor';
import FabricTemplateHtmlFrame from './fabricTemplates/FabricTemplateHtmlFrame';
import BlobPreview from './BlobPreview';
import { deleteFabricTemplate } from '../api/fabricTemplate.api';
import './fabricTemplates/fabricTemplates.css';

import { normalizeRunningPlanId } from '../constants/runningPlans';
import MultiProductTypeSelector from './MultiProductTypeSelector';
import FabricGarmentInsights, { getGarmentTypeInsights, GARMENT_INSIGHT_AUDIENCES } from './FabricGarmentInsights';
import { stripKidsGirlExcludedSelections } from '../utils/kidsProductCatalog';
import MultipleKitProgress from './MultipleKitProgress';
import TextureSelector from './TextureSelector';
import ProductSelectionModal from './ProductSelectionModal';
import FabricCaptureGuardrailsModal from '../features/fabricCaptureGuardrails/camera/FabricCaptureGuardrailsModal';

function buildGarmentThumbnailFileName(fileName) {
  const baseName = String(fileName || 'garment').replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${baseName}-${Date.now()}-thumbnail.jpg`;
}

function buildGarmentFileName(fileName) {
  const str = String(fileName || 'garment');
  const lastDot = str.lastIndexOf('.');
  const ext = lastDot >= 0 ? str.slice(lastDot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg' : 'jpg';
  const base = (lastDot >= 0 ? str.slice(0, lastDot) : str).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 60) || 'garment';
  return `garment-${Date.now()}-${base}.${ext}`;
}

function preferSpecificGsmWeight(...values) {
  for (const value of values) {
    const s = String(value || '').trim();
    if (!s || /<|>|\d\s*-\s*\d/.test(s)) continue;
    const labeled = s.match(/^(\d{2,3})\s*GSM$/i);
    const bare = s.match(/^(\d{2,3})$/);
    const n = labeled ? Number(labeled[1]) : bare ? Number(bare[1]) : NaN;
    if (Number.isFinite(n) && n >= 50 && n <= 600) return `${n} GSM`;
  }
  return values.find((v) => v) || null;
}

// Modern Horizontal Stepper Loader for Generate Kit
const GenerateKitModernLoader = ({ isVisible, progress, currentStep }) => {
  const steps = [
    {
      icon: <ShoppingBag size={32} />, label: 'Processing your product',
      desc: 'We\'re receiving your details and preparing your kit.',
    },
    {
      icon: <Palette size={32} />, label: 'Generating AI image',
      desc: 'Our AI is creating stunning visuals for your kit.',
    },
    {
      icon: <Sparkles size={32} />, label: 'Creating marketing content',
      desc: 'AI and automation tools are generating your content.',
    },
    {
      icon: <Zap size={32} />, label: 'Finalizing kit',
      desc: 'We\'re wrapping up and saving your kit.',
    },
  ];

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white shadow-2xl rounded-2xl px-6 py-8 sm:px-8 sm:py-10 max-w-lg w-full flex flex-col items-center relative">
        {/* Loader Icon - Mobile Optimized */}
        <div className="mb-6 flex flex-col items-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-xl bg-indigo-600 mb-3 animate-pulse">
            <Sparkles size={40} className="text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2 text-center">Submitting Your Marketing Kit</h2>
          <p className="text-sm sm:text-base text-slate-500 mb-3 text-center">Please wait while we process your request</p>
        </div>
        {/* Stepper */}
        <div className="flex justify-between w-full mb-6">
          {steps.map((step, idx) => (
            <div key={idx} className="flex flex-col items-center flex-1">
              <div className={`w-14 h-14 flex items-center justify-center rounded-full transition-all duration-300 mb-2
                ${idx < currentStep ? 'bg-indigo-600 text-white' : idx === currentStep ? 'bg-indigo-600 text-white ring-4 ring-indigo-100' : 'bg-slate-100 text-slate-400'}
              `}>
                {step.icon}
              </div>
              <div className={`text-center text-xs font-medium ${idx === currentStep ? 'text-slate-900' : 'text-slate-400'}`}>{step.label}</div>
            </div>
          ))}
        </div>
        {/* Progress Bar */}
        <div className="w-full h-2 bg-slate-100 rounded-full mb-6">
          <div className="h-2 rounded-full bg-indigo-600 transition-all duration-700" style={{width: `${progress}%`}}></div>
        </div>
        {/* Step Description */}
        <div className="mb-6 text-center">
          <div className="text-lg font-semibold text-slate-900 mb-1">{steps[currentStep]?.label}</div>
          <div className="text-sm text-slate-500">{steps[currentStep]?.desc}</div>
        </div>
        {/* Animated Dots */}
        <div className="flex justify-center items-center space-x-2 mb-2">
          <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce animation-delay-100"></div>
          <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce animation-delay-200"></div>
        </div>
        {/* Notification */}
        <div className="mt-2">
          <span className="inline-block bg-indigo-50 text-indigo-700 px-5 py-2 rounded-full text-sm font-medium">
            You will be notified when your kit is ready.
          </span>
        </div>
      </div>
    </div>
  );
};

const steps = [
  { label: 'Type', icon: Scissors },
  { label: 'Product', icon: ShoppingBag },
  { label: 'Content', icon: Sparkles },
];

const FABRIC_PROCESS_STEPS = [
  { key: 'uploading', label: 'Uploading your image' },
  { key: 'analyzing', label: 'Doing fabric analysis' },
  { key: 'saving', label: 'Saving to your materials' }
];

const ENVIRONMENT_OPTIONS = [
  { value: 'studio', label: 'Studio', description: 'Clean indoor studio lighting and neutral backdrop.' },
  { value: 'outdoor', label: 'Outdoor', description: 'Natural daylight environment for lifestyle-style visuals.' },
  { value: 'ghost-mannequin', label: 'Ghost Mannequin', description: 'Ecommerce ghost mannequin styling without visible model.' },
  { value: 'lifestyle', label: 'Lifestyle', description: 'Real-world contextual scene with story-driven framing.' },
  { value: 'custom', label: 'Custom', description: 'Use only your custom environment description below.' },
];

function normalizeColorVariantEntry(c) {
  if (!c || typeof c !== 'object') return null;
  const rawHex = (c.hex || c.colorHex || '').trim();
  if (!rawHex) return null;
  const hex = rawHex.startsWith('#') ? rawHex : `#${rawHex}`;
  return {
    colorName: c.colorName || c.name || c.label || 'Color',
    hex,
  };
}

function aggregateColorVariantsFromShadeCards(fabricShadeCards) {
  const allColors = [];
  const seenHexes = new Set();
  (fabricShadeCards || []).forEach((card) => {
    if (!card?.extractedColors || !Array.isArray(card.extractedColors)) return;
    card.extractedColors.forEach((color) => {
      const entry = normalizeColorVariantEntry(color);
      if (!entry) return;
      const hexKey = entry.hex.toLowerCase();
      if (seenHexes.has(hexKey)) return;
      seenHexes.add(hexKey);
      allColors.push({
        colorName: entry.colorName || `Color ${allColors.length + 1}`,
        hex: entry.hex,
      });
    });
  });
  return allColors;
}

function colorVariantsFingerprint(variants) {
  if (!Array.isArray(variants) || variants.length === 0) return '';
  return variants
    .map((c) => `${(c.hex || '').toLowerCase()}|${c.colorName || ''}`)
    .sort()
    .join(';');
}

function colorVariantsEqual(a, b) {
  return colorVariantsFingerprint(a) === colorVariantsFingerprint(b);
}

/** Prefer colors embedded on shade cards; otherwise use saved product colorVariants. */
function aggregateColorVariantsForFabric(fabricShadeCards, productColorVariants) {
  const fromCards = aggregateColorVariantsFromShadeCards(fabricShadeCards);
  if (fromCards.length > 0) return fromCards;
  const hasEmbeddedCardColors = (fabricShadeCards || []).some(
    (c) => Array.isArray(c?.extractedColors) && c.extractedColors.length > 0
  );
  if (hasEmbeddedCardColors) return [];
  return (productColorVariants || []).map(normalizeColorVariantEntry).filter(Boolean);
}

const GenerateKitWizard = ({
  branding,
  setBranding,
  brandLogoUploading,
  handleGenerateKitBrandingChange,
  handleBrandLogoChange,
  openBrandModal,
  availableFonts,
  productDetails,
  setProductDetails,
  productImageUploading,
  fabricAnalysisLoading,
  fabricUploadPhase = null,
  handleProductInputChange,
  handleProductImageChange,
  openProductModal,
  availableTargetAudiences,
  contentPreferences,
  setContentPreferences,
  handleContentPreferenceChange,
  handleContentTypeToggle,
  availableTones,
  selectedModel,
  setModelModalOpen,
  availableModels,
  modelModalOpen,
  setSelectedModel,
  FilePreview,
  InputField,
  handleGenerateRealtimeContent,
  openGenerateAndSaveKitModal,
  isLoading,
  showNotification,
  setActiveView,
  refreshKits,
  viewKitDetails,
  savedKits,
  showInsufficientCreditsModal,
  liteMode = false,
  fourKEnabled = false,
  runningPlan = 'free',
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  // Find first non-disabled category - default to Women Clothing
  const firstEnabledCategory = PRODUCT_CATEGORIES_OPTIMIZED.find(cat => cat.value === 'women-clothing') || PRODUCT_CATEGORIES_OPTIMIZED.find(cat => !cat.disabled) || PRODUCT_CATEGORIES_OPTIMIZED[0];
  const [selectedCategory, setSelectedCategory] = useState(firstEnabledCategory);

  const [showCategoryTip, setShowCategoryTip] = useState(false);

  // New state for fabric type selection - only unstitched
  const [fabricType, setFabricType] = useState('unstitched');
  const [isFabricAnalysisOpen, setIsFabricAnalysisOpen] = useState(true);
  const [isAiRecommendationsOpen, setIsAiRecommendationsOpen] = useState(false);
  const [showAllAiRecommendations, setShowAllAiRecommendations] = useState(false);

  // Multiple product types state
  const [selectedTypes, setSelectedTypes] = useState([]);
  // Keep fullscreen product-type modal on phones/tablets; inline only on desktop.
  const [useInlineProductSelector, setUseInlineProductSelector] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  const [showMultipleTypeTip, setShowMultipleTypeTip] = useState(false);
  const [generationMode, setGenerationMode] = useState('multiple'); // Always use multiple types

  // Camera state
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const [cameraCaptureTarget, setCameraCaptureTarget] = useState('fabric'); // 'fabric' | 'model'
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isMultipleKitGenerating, setIsMultipleKitGenerating] = useState(false);
  const [multipleKitProgress, setMultipleKitProgress] = useState({
    current: 0,
    total: 0,
    productType: '',
    status: 'pending',
    error: null
  });

  // Loader state
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [loaderStep, setLoaderStep] = useState(0);

  // Advanced settings state
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [showTextureTip, setShowTextureTip] = useState(false);
  const [enableCloseupshot, setEnableCloseupshot] = useState(false);
  const closeupShotLocked = (normalizeRunningPlanId(runningPlan) || 'free') === 'free';
  const closeupShotEnabled = Boolean(enableCloseupshot) && !closeupShotLocked;
  const shotType = closeupShotEnabled ? 'closeup' : 'zoomout';
  const [generateMultiColorImage, setGenerateMultiColorImage] = useState(false);
  const [environmentPreset, setEnvironmentPreset] = useState(productDetails?.scenePreset || 'studio');
  const [customEnvironmentPrompt, setCustomEnvironmentPrompt] = useState(productDetails?.scene || '');
  const [modelImageUploading, setModelImageUploading] = useState(false);
  const brandLogoInputRef = useRef(null);
  const resolvedEnvironmentPreset = useMemo(
    () => (environmentPreset === 'custom' ? null : (environmentPreset || 'studio')),
    [environmentPreset]
  );

  // Inspirational design upload state
  const [inspirationalDesignUploading, setInspirationalDesignUploading] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [recentFabrics, setRecentFabrics] = useState([]);
  const [loadingRecentFabrics, setLoadingRecentFabrics] = useState(false);
  
  // Product input method state
  const [productInputMethod, setProductInputMethod] = useState('types'); // 'types' or 'inspiration'
  
  // Kid age state for kids categories
  const [selectedKidAge, setSelectedKidAge] = useState('kids'); // Default: Kids (4-12 years)
  
  const selectedVariantCount = productDetails?.selectedVariants?.length || 0;
  const fallbackVariantCount = productDetails?.colorVariants?.length ? (productDetails.colorVariants.length + 1) : 0; // +1 for primary
  const totalColorShades = selectedVariantCount || fallbackVariantCount;
  const hasMultipleColorVariants = totalColorShades > 1;

  useEffect(() => {
    if (!hasMultipleColorVariants && generateMultiColorImage) {
      setGenerateMultiColorImage(false);
    }
  }, [hasMultipleColorVariants, generateMultiColorImage]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setUseInlineProductSelector(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Inspiration design modal state
  const [isInspirationModalOpen, setIsInspirationModalOpen] = useState(false);
  const [isDesignTemplateModalOpen, setIsDesignTemplateModalOpen] = useState(false);
  const [selectedFabricTemplate, setSelectedFabricTemplate] = useState(null);
  // Garment designs (from fabric analysis) chosen for the fabric template (up to 3).
  const [selectedFabricTemplateGarments, setSelectedFabricTemplateGarments] = useState([]);
  const MAX_TEMPLATE_GARMENTS = 3;
  // Local-only fabric properties for this catalog generation. Never written back to the template.
  const [templatePropertyDraft, setTemplatePropertyDraft] = useState(null);
  const templatePropertiesDirtyRef = useRef(false);
  // Set true once a generate is attempted, so the inline template setup shows validation errors.
  const [templateSetupAttempted, setTemplateSetupAttempted] = useState(false);
  const [previewFabricTemplate, setPreviewFabricTemplate] = useState(null);
  const [showAddFabricTemplateModal, setShowAddFabricTemplateModal] = useState(false);
  const [deletingFabricTemplateId, setDeletingFabricTemplateId] = useState(null);
  const { templates: fabricTemplates, refresh: refreshFabricTemplates } = useFabricTemplates();
  const [selectedInspirationDesign, setSelectedInspirationDesign] = useState(null);
  const [inspirationSearchTerm, setInspirationSearchTerm] = useState('');
  const [myInspirations, setMyInspirations] = useState([]);
  const [inspirationsLoading, setInspirationsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Add new inspiration modal state
  const [showAddInspirationModal, setShowAddInspirationModal] = useState(false);
  const [newInspirationData, setNewInspirationData] = useState({
    imageUrl: '',
    productName: '',
    imageFile: null
  });
  const [addingInspiration, setAddingInspiration] = useState(false);

  /** Default fabric swatch builder (preset + color + properties) */
  const [defaultFabricDraft, setDefaultFabricDraft] = useState(null);
  const [defaultFabricApplying, setDefaultFabricApplying] = useState(false);

  // Fabric properties state
  const [fabricWeight, setFabricWeight] = useState('');
  const [fabricShadeCards, setFabricShadeCards] = useState([]);
  const [isUploadingShadeCard, setIsUploadingShadeCard] = useState(false);
  const [isExtractingColors, setIsExtractingColors] = useState(false);
  const [showExtractionSuccess, setShowExtractionSuccess] = useState(false);
  const [extractedColorVariants, setExtractedColorVariants] = useState([]);
  const shadeCardInputRef = useRef(null);
  const colorVariantsSyncFingerprintRef = useRef('');

  // Initialize productDetails with default values if not already set
  useEffect(() => {
    if (!productDetails.category || !productDetails.type) {
      setProductDetails(prev => ({
        ...prev,
        category: firstEnabledCategory.value,
        // Keep product type unselected by default (user must pick explicitly)
        type: prev.type || null
      }));
    }
  }, []);

  // Find texture object from fabric_type string (for TextureSelector in fabric analysis)
  const findTextureByFabricType = (fabricType) => {
    if (!fabricType) return null;
    for (const cat of FABRIC_TEXTURES.fabricTextures || []) {
      const t = cat.types?.find(x => x.name === fabricType);
      if (t) return { ...t, categoryName: cat.category };
    }
    return { name: fabricType, categoryName: 'Other', description: '' };
  };

  // Effective fabric texture for API: prefer Fabric Texture dropdown, fallback to fabric type from analysis
  const getEffectiveFabricTexture = () => {
    if (selectedTexture) return selectedTexture;
    const ft = productDetails?.fabricAnalysis?.fabric_analysis?.fabric_type;
    return findTextureByFabricType(ft);
  };

  // Fabric Analysis editable field handlers (updates productDetails.fabricAnalysis)
  const handleFabricAnalysisFieldChange = (section, field, value) => {
    setProductDetails(prev => {
      const analysis = prev.fabricAnalysis || {};
      const updated = { ...analysis };
      if (!updated[section]) updated[section] = {};
      updated[section] = { ...updated[section], [field]: value };
      return { ...prev, fabricAnalysis: updated };
    });
  };

  const selectDefaultFabricPreset = (preset) => {
    setDefaultFabricDraft({
      presetId: preset.id,
      hex: '#5b6b8c',
      fabric_type: preset.fabric_type,
      weave_or_knit: preset.weave_or_knit,
      opacity: preset.opacity,
      stretch: preset.stretch,
      gsm_weight: preset.gsm_weight,
    });
  };

  const updateDefaultFabricDraft = (patch) => {
    setDefaultFabricDraft((d) => (d ? { ...d, ...patch } : d));
  };

  const applyDefaultFabricSwatch = async () => {
    if (!defaultFabricDraft) return;
    setDefaultFabricApplying(true);
    try {
      resetShadeCardSelection();
      const tex = findTextureByFabricType(defaultFabricDraft.fabric_type);
      setFabricWeight(defaultFabricDraft.gsm_weight);
      setSelectedTexture(tex);
      setProductDetails((prev) => ({
        ...prev,
        fabricWeight: defaultFabricDraft.gsm_weight,
        fabricTexture: tex
          ? {
              name: tex.name,
              description: tex.description || '',
              categoryName: tex.categoryName || '',
            }
          : null,
      }));
      const rawHex = (defaultFabricDraft.hex || '').trim();
      const hexMatch = rawHex.match(/^#?([0-9a-fA-F]{6})$/i);
      const hex = hexMatch ? `#${hexMatch[1]}` : '#5b6b8c';
      const file = await createFabricSwatchImageFile(hex);
      await handleProductImageChange(file, {
        fabricAnalysisOverrides: {
          fabric_analysis: {
            fabric_type: defaultFabricDraft.fabric_type,
            weave_or_knit: defaultFabricDraft.weave_or_knit,
            opacity: defaultFabricDraft.opacity,
            stretch: defaultFabricDraft.stretch,
            gsm_weight: defaultFabricDraft.gsm_weight,
          },
          visual_characteristics: {
            primary_colors: [`Swatch ${hex}`],
          },
        },
      });
    } catch (err) {
      console.error(err);
      if (showNotification) {
        showNotification('Could not create fabric swatch. Try again.', 'error');
      }
    } finally {
      setDefaultFabricApplying(false);
    }
  };


  // Camera functions
  const openCamera = async (captureTarget = 'fabric', preferredFacingMode = null) => {
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        showNotification?.('Camera is not available on this device/browser.', 'error');
        return;
      }

      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const targetFacingMode = preferredFacingMode || cameraFacingMode;
      setCameraCaptureTarget(captureTarget);
      if (preferredFacingMode) {
        setCameraFacingMode(preferredFacingMode);
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: targetFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
      setIsCameraOpen(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      const name = err?.name || '';
      const msg =
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'Camera permission denied. Please enable camera permission in Settings and try again.'
          : name === 'NotFoundError'
            ? 'No camera found on this device.'
            : 'Unable to access camera. Please check permissions.';
      showNotification?.(msg, 'error');
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraOpen(false);
  };

  const switchCamera = async () => {
    try {
      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      // Toggle camera facing mode
      const newFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';
      setCameraFacingMode(newFacingMode);
      
      // Get new stream with different camera
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
    } catch (err) {
      console.error('Error switching camera:', err);
      showNotification('Unable to switch camera. Please try again.', 'error');
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Check if video is ready and playing
    if (video.readyState !== video.HAVE_ENOUGH_DATA || video.videoWidth === 0 || video.videoHeight === 0) {
      console.error('Video not ready');
      showNotification('Camera is not ready. Please wait a moment and try again.', 'error');
      return;
    }

    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          // Create a File object from the blob
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
          
          // Close camera first
          closeCamera();
          
          // Use setTimeout to ensure camera closes before processing
          setTimeout(async () => {
            if (cameraCaptureTarget === 'model') {
              await handleModelImageUpload(file);
            } else {
              resetShadeCardSelection();
              setDefaultFabricDraft(null);
              handleProductImageChange(file);
            }
          }, 200);
        } catch (error) {
          console.error('Error processing captured photo:', error);
          showNotification('Failed to process captured photo. Please try again.', 'error');
          closeCamera();
        }
      } else {
        console.error('Failed to create blob from canvas');
        showNotification('Failed to capture photo. Please try again.', 'error');
        closeCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  // Clean up stream on unmount or when camera closes
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Set video srcObject when stream is available
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Check if user is admin
  useEffect(() => {
    const userRole = localStorage.getItem('role')?.toLowerCase();
    setIsAdmin(userRole === 'admin');
  }, []);

  // Load user's inspirational designs (or all users' designs if admin)
  const loadMyInspirations = async () => {
    try {
      setInspirationsLoading(true);
      const userDesigns = await getInspirationalDesigns();
      setMyInspirations(userDesigns);
    } catch (error) {
      console.error('Error loading my inspirations:', error);
      showNotification("Failed to load your inspirations.", "error");
    } finally {
      setInspirationsLoading(false);
    }
  };

  const applyInspirationDesignSelection = ({ id, name, category, imageUrl, htmlContent = '' }) => {
    const isPersistedId = id != null && !String(id).startsWith('template-');
    setSelectedInspirationDesign({ id, name, category, imageUrl, htmlContent });
    setProductInputMethod('inspiration');
    setSelectedTypes([]);
    if (isPersistedId) {
      setSelectedFabricTemplate(null);
      setTemplatePropertyDraft(null);
      templatePropertiesDirtyRef.current = false;
    }
    setProductDetails((prev) => ({
      ...prev,
      inspirationalDesign: imageUrl || null,
      inspirationalDesignId: isPersistedId ? String(id) : null,
      inspirationalProductType: name,
      name,
      type: name,
      description: `Generated content for ${name}`,
    }));
  };

  const applyFabricTemplateSelection = (template) => {
    if (!template) return;
    // Prefer full catalog/flyer layout art so generation can rebuild the TEMPLATE PAGE structure.
    // Fall back to design image when a custom template only has a design upload.
    const templateImage =
      template.thumbnail ||
      template.thumbnailUrl ||
      template.designImage ||
      template.designImageUrl ||
      '';
    const styleLabel = template.productType || template.name || 'Multiple garment categories';
    setSelectedFabricTemplate(template);
    setSelectedFabricTemplateGarments([]);
    setEnableCloseupshot(false);
    setTemplateSetupAttempted(false);
    templatePropertiesDirtyRef.current = false;
    setTemplatePropertyDraft(
      snapshotTemplateFabricProperties({
        productDetails,
        extractedColorVariants,
        selectedTexture,
      })
    );
    setSelectedInspirationDesign({
      id: `template-${template.id || template._id}`,
      name: styleLabel,
      category: `Fabric Template · ${template.name || 'Custom'}`,
      imageUrl: templateImage,
      htmlContent: template.templateKind === 'html' ? template.htmlContent || '' : '',
    });
    setProductInputMethod('inspiration');
    setSelectedTypes([]);
    setProductDetails((prev) => {
      const fabricProduct = prev.selectedProduct || null;
      const realFabricName =
        (fabricProduct?.name && String(fabricProduct.name).trim()) ||
        (prev.name && !String(prev.name).startsWith('Fabric Template') ? String(prev.name).trim() : '') ||
        '';
      const realFabricDescription =
        (fabricProduct?.description && String(fabricProduct.description).trim()) ||
        (prev.description && !String(prev.description).startsWith('Generated content for')
          ? String(prev.description).trim()
          : '') ||
        '';
      return {
        ...prev,
        inspirationalDesign: templateImage || null,
        inspirationalDesignId: null,
        inspirationalProductType: styleLabel,
        // Keep garment style on `type`; preserve real fabric identity for name/description.
        // Do not invent a fabric name from the template style label.
        type: styleLabel,
        name: realFabricName || prev.name || '',
        description: realFabricDescription || prev.description || '',
      };
    });
  };

  const buildGenerationAdvancedSettings = (overrides = {}) => {
    const t = getEffectiveFabricTexture();
    const fabricProduct = productDetails?.selectedProduct || null;
    const analysis = productDetails?.fabricAnalysis || fabricProduct?.fabricAnalysis || null;
    const templateOverrides =
      selectedFabricTemplate && templatePropertyDraft
        ? templatePropertiesToGenerationOverrides(templatePropertyDraft)
        : {};
    const shadeNames = templateOverrides.fabricCatalogUseProvidedShadesOnly
      ? templateOverrides.colorShadeNames || []
      : [
          ...(Array.isArray(productDetails?.colorVariants)
            ? productDetails.colorVariants.map((v) => v?.colorName || v?.hex).filter(Boolean)
            : []),
          ...(Array.isArray(extractedColorVariants)
            ? extractedColorVariants.map((v) => v?.colorName || v?.hex).filter(Boolean)
            : []),
        ].filter((name, index, arr) => arr.indexOf(name) === index);

    return {
      fabricTexture: t?.name || null,
      fabricTextureCategory: t?.categoryName || null,
      fabricTextureDescription: t?.description || null,
      opacity:
        analysis?.fabric_analysis?.opacity ||
        productDetails?.fabricAnalysis?.fabric_analysis?.opacity ||
        null,
      stretch:
        analysis?.fabric_analysis?.stretch ||
        productDetails?.fabricAnalysis?.fabric_analysis?.stretch ||
        null,
      fabricWeight: preferSpecificGsmWeight(
        selectedGsmWeight,
        analysis?.fabric_analysis?.gsm_weight,
        fabricProduct?.fabricWeight
      ),
      fabricWidth: fabricProduct?.width || fabricProduct?.fabricWidth || productDetails?.width || null,
      fabricAnalysis: analysis,
      fabricShadeCards: productDetails.fabricShadeCards || fabricShadeCards || null,
      fabricProductName:
        fabricProduct?.name ||
        (selectedFabricTemplate ? productDetails?.name : null) ||
        null,
      fabricProductDescription:
        fabricProduct?.description ||
        (selectedFabricTemplate ? productDetails?.description : null) ||
        null,
      colorShadeNames: shadeNames,
      environmentPreset: resolvedEnvironmentPreset,
      environmentPrompt: customEnvironmentPrompt?.trim() || null,
      generateMultiColorImage: generateMultiColorImage && hasMultipleColorVariants,
      isFabricTemplate: Boolean(selectedFabricTemplate),
      fabricTemplateId: selectedFabricTemplate?.id || selectedFabricTemplate?._id || null,
      fabricTemplateName: selectedFabricTemplate?.name || null,
      fabricTemplateStyle:
        (selectedFabricTemplateGarments.length
          ? selectedFabricTemplateGarments.map((g) => g.garment).join(', ')
          : '') ||
        selectedFabricTemplate?.productType ||
        productDetails?.inspirationalProductType ||
        selectedFabricTemplate?.category ||
        selectedFabricTemplate?.name ||
        null,
      fabricTemplateGarments: selectedFabricTemplateGarments.length ? selectedFabricTemplateGarments : null,
      conceptCount: 3,
      catalogAspectRatio: '3:4',
      ...templateOverrides,
      ...overrides,
      enableCloseupshot: closeupShotLocked
        ? false
        : overrides.enableCloseupshot !== undefined
          ? Boolean(overrides.enableCloseupshot) && !selectedFabricTemplate
          : closeupShotEnabled && !selectedFabricTemplate,
    };
  };

  const filteredDesignTemplates = fabricTemplates;

  // Garment recommendations from fabric analysis, grouped by audience for the setup modal's tabs
  // (Women, Men, Kids Boy, Kids Girl). Uses the shared normalizer, which reads the newer
  // `garment_type_insights` and falls back to the legacy men's/women's recommendation arrays.
  // Only audiences that actually have recommendations become tabs; the audience matching the
  // current category selection is ordered first.
  const fabricTemplateGarmentGroups = useMemo(() => {
    const insights = getGarmentTypeInsights(productDetails?.fabricAnalysis);
    const orderedAudiences = [
      ...GARMENT_INSIGHT_AUDIENCES.filter((a) => a.category === selectedCategory?.value),
      ...GARMENT_INSIGHT_AUDIENCES.filter((a) => a.category !== selectedCategory?.value),
    ];
    return orderedAudiences
      .map((audience) => {
        const seen = new Set();
        const items = (insights[audience.key] || [])
          .map((item) => ({ garment: (item?.type_label || '').trim(), reason: item?.reason || '' }))
          .filter((it) => {
            const key = it.garment.toLowerCase();
            if (!it.garment || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        return { key: audience.key, label: audience.label, items };
      })
      .filter((group) => group.items.length > 0);
  }, [productDetails?.fabricAnalysis, selectedCategory?.value]);

  useEffect(() => {
    if (!selectedFabricTemplate) {
      templatePropertiesDirtyRef.current = false;
      setTemplatePropertyDraft(null);
      return;
    }
    if (templatePropertiesDirtyRef.current) return;
    setTemplatePropertyDraft(
      snapshotTemplateFabricProperties({
        productDetails,
        extractedColorVariants,
        selectedTexture,
      })
    );
  }, [
    selectedFabricTemplate,
    productDetails?.fabricAnalysis,
    productDetails?.fabricWeight,
    productDetails?.colorVariants,
    productDetails?.width,
    extractedColorVariants,
    selectedTexture,
  ]);

  // Toggle a garment design in the inline fabric template setup (max 3).
  // Each selection is scoped to the audience tab it was picked from, so the same
  // garment name can be featured for different genders/ages, and the backend can
  // render the correct model per concept.
  const toggleTemplateGarment = (name, audienceKey, audienceLabel) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setSelectedFabricTemplateGarments((prev) => {
      const exists = prev.some((g) => g.garment === trimmed && g.audienceKey === audienceKey);
      if (exists) {
        return prev.filter((g) => !(g.garment === trimmed && g.audienceKey === audienceKey));
      }
      if (prev.length >= MAX_TEMPLATE_GARMENTS) return prev;
      return [...prev, { garment: trimmed, audienceKey: audienceKey || '', audienceLabel: audienceLabel || '' }];
    });
  };

  const handleDeleteFabricTemplate = async (template) => {
    const deleteId = template?._id || template?.id;
    if (!deleteId) {
      showNotification?.('Cannot delete this template', 'error');
      return;
    }
    if (!window.confirm(`Delete template “${template.name}”?`)) return;
    setDeletingFabricTemplateId(deleteId);
    try {
      await deleteFabricTemplate(deleteId);
      showNotification?.('Template deleted', 'success');
      if (previewFabricTemplate?.id === template.id) setPreviewFabricTemplate(null);
      if (selectedFabricTemplate?.id === template.id) {
        setSelectedFabricTemplate(null);
        setTemplatePropertyDraft(null);
        templatePropertiesDirtyRef.current = false;
        if (String(selectedInspirationDesign?.id || '').startsWith('template-')) {
          setSelectedInspirationDesign(null);
          setProductInputMethod('types');
          setProductDetails((prev) => ({
            ...prev,
            inspirationalDesign: null,
            inspirationalDesignId: null,
            inspirationalProductType: '',
            name: '',
            type: '',
            description: '',
          }));
        }
      }
      await refreshFabricTemplates();
    } catch (err) {
      showNotification?.(err?.response?.data?.message || 'Failed to delete template', 'error');
    } finally {
      setDeletingFabricTemplateId(null);
    }
  };

  // Handle adding new inspiration
  const handleAddNewInspiration = async () => {
    if (!newInspirationData.productName.trim()) {
      showNotification("Please enter a product name", "error");
      return;
    }

    if (!newInspirationData.imageFile && !newInspirationData.imageUrl.trim()) {
      showNotification("Please select an image or provide an image URL", "error");
      return;
    }

    setAddingInspiration(true);
    try {
      let imageUrl = newInspirationData.imageUrl;
      let thumbnailUrl = '';

      // Upload image to S3 if a file was selected
      if (newInspirationData.imageFile) {
        showNotification("Uploading image to S3...", "info");
        const uploadResult = await uploadImageToS3(newInspirationData.imageFile, {
          folder: 'inspirational-designs',
          fileName: buildGarmentFileName(newInspirationData.imageFile.name),
        });
        if (uploadResult.success) {
          imageUrl = uploadResult.url;
        } else {
          throw new Error('Failed to upload image');
        }

        try {
          const thumbnailFile = await createThumbnailImageFile(newInspirationData.imageFile);
          const thumbnailUpload = await uploadImageToS3(thumbnailFile, {
            folder: 'inspirational-designs/thumbnails',
            fileName: buildGarmentThumbnailFileName(newInspirationData.imageFile.name),
          });
          if (thumbnailUpload.success) {
            thumbnailUrl = thumbnailUpload.url;
          }
        } catch (thumbnailError) {
          console.warn('Garment thumbnail creation/upload failed; falling back to original image:', thumbnailError);
        }
      }

      // Create the inspirational design
      await createInspirationalDesign({
        imageUrl: imageUrl,
        thumbnailUrl,
        productName: newInspirationData.productName
      });

      // Reset form and reload inspirations
      setNewInspirationData({ imageUrl: '', productName: '', imageFile: null });
      setShowAddInspirationModal(false);
      await loadMyInspirations();
      showNotification("Inspirational design added successfully!", "success");
    } catch (error) {
      console.error('Error adding inspirational design:', error);
      showNotification("Failed to add inspirational design. Please try again.", "error");
    } finally {
      setAddingInspiration(false);
    }
  };

  // Handle file selection for new inspiration
  const handleInspirationFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewInspirationData({ ...newInspirationData, imageFile: file, imageUrl: '' });
    }
  };


  // Handle inspirational design upload to S3
  const handleInspirationalDesignUpload = async (file) => {
    try {
      setInspirationalDesignUploading(true);
      showNotification("Uploading inspirational design to S3...", "info");

      // Upload to S3
      const uploadResult = await uploadImageToS3(file, {
        folder: 'inspirational-designs',
        fileName: buildGarmentFileName(file.name).replace(/^garment-/, 'inspirational-'),
        onProgress: (progress) => {
          console.log('Inspirational design upload progress:', progress);
        }
      });

      // Save to library first so we get an id for linking kits on this garment detail page
      let createdInspiration = null;
      let thumbnailUrl = '';
      try {
        const thumbnailFile = await createThumbnailImageFile(file);
        const thumbnailUpload = await uploadImageToS3(thumbnailFile, {
          folder: 'inspirational-designs/thumbnails',
          fileName: buildGarmentThumbnailFileName(file.name),
        });
        if (thumbnailUpload.success) {
          thumbnailUrl = thumbnailUpload.url;
        }
      } catch (thumbnailError) {
        console.warn('Garment thumbnail creation/upload failed; falling back to original image:', thumbnailError);
      }

      try {
        const rawName = file?.name?.replace(/\.[^/.]+$/, '') || '';
        const productName = rawName.trim().slice(0, 120) || 'Kit inspiration';
        createdInspiration = await createInspirationalDesign({
          imageUrl: uploadResult.url,
          thumbnailUrl,
          productName
        });
      } catch (persistErr) {
        console.warn('Inspirational design uploaded but not saved to library:', persistErr);
      }

      setProductDetails((prev) => ({
        ...prev,
        inspirationalDesign: uploadResult.url,
        ...(createdInspiration?._id ? { inspirationalDesignId: String(createdInspiration._id) } : {}),
        ...(createdInspiration?.productName
          ? {
              name: prev.name?.trim() ? prev.name : createdInspiration.productName,
              type: prev.type || createdInspiration.productName,
              description:
                prev.description?.trim() ||
                `Generated content for ${createdInspiration.productName}`
            }
          : {})
      }));

      if (createdInspiration?._id) {
        setSelectedInspirationDesign({
          id: createdInspiration._id,
          name: createdInspiration.productName,
          category: 'My inspiration',
          imageUrl: uploadResult.url,
          thumbnailUrl: createdInspiration.thumbnailUrl || thumbnailUrl
        });
        setProductInputMethod('inspiration');
      }

      showNotification("Inspirational design uploaded successfully!", "success");
      console.log('Inspirational design uploaded to S3:', uploadResult.url);

    } catch (error) {
      console.error('Error uploading inspirational design:', error);
      showNotification("Failed to upload inspirational design. Please try again.", "error");
    } finally {
      setInspirationalDesignUploading(false);
    }
  };

  const handleModelImageUpload = async (file) => {
    if (!file) return;
    try {
      setModelImageUploading(true);
      showNotification("Uploading model image...", "info");

      const compressed = await compressImage(file);
      const uploadResult = await uploadImageToS3(compressed, {
        folder: 'model-images',
        fileName: `model-${Date.now()}-${file.name}`,
      });

      if (!uploadResult?.success || !uploadResult?.url) {
        throw new Error('Failed to upload model image');
      }

      setProductDetails(prev => ({ ...prev, modelImage: uploadResult.url }));
      showNotification("Model image uploaded successfully!", "success");
    } catch (error) {
      console.error('Error uploading model image:', error);
      showNotification("Failed to upload model image. Please try again.", "error");
    } finally {
      setModelImageUploading(false);
    }
  };

  // Sync category picker when fabric/product sets category + type (avoid [productDetails] — new parent object every field update causes infinite loops)
  useEffect(() => {
    const catVal = productDetails?.category;
    const typeVal = productDetails?.type;
    if (!catVal || !typeVal) return;
    const category = PRODUCT_CATEGORIES_OPTIMIZED.find((cat) => cat.value === catVal);
    if (!category || category.disabled) return;
    setSelectedCategory((prev) => (prev?.value === category.value ? prev : category));
  }, [productDetails?.category, productDetails?.type]);

  useEffect(() => {
    if (isLoading) {
      setLoaderProgress(0);
      setLoaderStep(0);
      const progressInterval = setInterval(() => {
        setLoaderProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 25;
        });
      }, 1200);
      const stepInterval = setInterval(() => {
        setLoaderStep(prev => {
          if (prev >= 3) {
            clearInterval(stepInterval);
            return 3;
          }
          return prev + 1;
        });
      }, 2000);
      return () => {
        clearInterval(progressInterval);
        clearInterval(stepInterval);
      };
    } else {
      setLoaderProgress(0);
      setLoaderStep(0);
    }
  }, [isLoading]);


  const goNext = () => {
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  };
  
  const goBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const hasFabricImageUploaded = typeof productDetails?.image === 'string' && productDetails.image.startsWith('http');
  const hasFabricAnalysisCompleted = Boolean(
    productDetails?.fabricAnalysis &&
    Object.keys(productDetails.fabricAnalysis).length > 0
  );

  const showFabricPipelineOverlay =
    currentStep === 0 &&
    (fabricUploadPhase != null || productImageUploading || fabricAnalysisLoading);

  let fabricPipelineActiveIndex = 0;
  if (fabricUploadPhase) {
    const i = FABRIC_PROCESS_STEPS.findIndex((s) => s.key === fabricUploadPhase);
    fabricPipelineActiveIndex = i >= 0 ? i : 0;
  } else if (fabricAnalysisLoading) {
    fabricPipelineActiveIndex = 2;
  } else if (productImageUploading) {
    fabricPipelineActiveIndex = 0;
  }
  const selectedGsmWeight = preferSpecificGsmWeight(
    productDetails?.fabricAnalysis?.fabric_analysis?.gsm_weight,
    productDetails.fabricWeight,
    fabricWeight
  );

  const handleCategoryChange = (e) => {
    const selected = PRODUCT_CATEGORIES_OPTIMIZED.find(opt => opt.value === e.target.value);
    
    // Prevent selecting disabled categories
    if (selected.disabled) {
      return;
    }
    
    setSelectedCategory(selected);
    
    if (selected.value === 'kids-girl') {
      setSelectedTypes((prev) => stripKidsGirlExcludedSelections(prev));
    }
    
    // Update product details with category, type, and kid age if applicable
    setProductDetails(prev => {
      const updatedDetails = {
        ...prev,
        category: selected.value,
        // Keep product type unselected when category changes (no auto selection)
        type: null
      };
      
      // Add kid age for kids categories
      if (selected.value === 'kids-boy' || selected.value === 'kids-girl') {
        updatedDetails.kidAge = selectedKidAge;
      } else {
        // Remove kid age for non-kids categories
        delete updatedDetails.kidAge;
      }
      
      return updatedDetails;
    });
  };

  const handleCategorySelect = (value) => {
    handleCategoryChange({ target: { value } });
  };



  // Handle kid age change
  const handleKidAgeChange = (e) => {
    const newAge = e.target.value;
    setSelectedKidAge(newAge);
    
    // Update product details with new kid age if kids category is selected
    if (selectedCategory.value === 'kids-boy' || selectedCategory.value === 'kids-girl') {
      setProductDetails(prev => ({
        ...prev,
        kidAge: newAge
      }));
    }

    // If toddler is selected, force only one product type: Toddler Dress
    if ((selectedCategory.value === 'kids-boy' || selectedCategory.value === 'kids-girl') && newAge === 'toddlers') {
      setSelectedTypes([
        {
          label: 'Toddler Dress',
          value: 'toddler-dress',
          img: null,
          parentType: null,
          parentLabel: null,
          finalValue: 'toddler-dress',
          finalLabel: 'Toddler Dress',
          category: selectedCategory?.value,
          categoryLabel: selectedCategory?.label,
        },
      ]);
    } else if (
      (selectedCategory.value === 'kids-boy' || selectedCategory.value === 'kids-girl') &&
      (newAge === 'kids' || newAge === 'teens')
    ) {
      // Adult-catalog types replace toddler-only selection when age range changes.
      setSelectedTypes((prev) => {
        let next = prev.filter((t) => t.value !== 'toddler-dress');
        if (selectedCategory.value === 'kids-girl') {
          next = stripKidsGirlExcludedSelections(next);
        }
        return next;
      });
    }
  };

  const handleMultipleTypesChange = useCallback((types) => {
    setSelectedTypes(types);
  }, []);

  const handleProductTypesChange = useCallback((types) => {
    setProductInputMethod('types');
    handleMultipleTypesChange(types);
  }, [handleMultipleTypesChange]);

  // Handle variant selection/deselection
  const handleVariantToggle = (variantIndex) => {
    setProductDetails(prev => {
      const updatedVariants = [...prev.selectedVariants];
      
      // Remove the variant at the specified index
      const deselectedVariant = updatedVariants.splice(variantIndex, 1)[0];
      
      console.log('🔄 Deselected variant:', deselectedVariant);
      console.log('📊 Remaining selected variants:', updatedVariants.length);
      
      // Show notification
      if (showNotification) {
        showNotification(`${deselectedVariant.colorName || 'Variant'} deselected from kit generation`, 'info');
      }
      
      // If no variants are selected, clear the entire product selection
      if (updatedVariants.length === 0) {
        console.log('🗑️ All variants deselected, clearing product selection');
        if (showNotification) {
          showNotification('All variants deselected. Material removed. Please choose a new material.', 'info');
        }
        
        return {
          ...prev,
          selectedVariants: [],
          selectedProduct: null,
          availableVariants: [],
          image: null,
          category: null,
          colorVariants: [],
          selectedVariantIndex: 0
        };
      }
      
      return {
        ...prev,
        selectedVariants: updatedVariants
      };
    });
  };

  // Handle variant reselection
  const handleVariantReselect = (variant) => {
    setProductDetails(prev => {
      const updatedVariants = [...prev.selectedVariants, variant];
      
      console.log('✅ Reselected variant:', variant);
      console.log('📊 Total selected variants:', updatedVariants.length);
      
      // Show notification
      if (showNotification) {
        showNotification(`${variant.colorName || 'Variant'} added back to kit generation`, 'success');
      }
      
      return {
        ...prev,
        selectedVariants: updatedVariants
      };
    });
  };

  const handleMultipleKitGeneration = async () => {
    if (!productDetails.image || !productDetails.image.startsWith('http')) {
      return;
    }

    // For unstitched fabric, require either product types, inspirational design, or fabric template
    if (true) {
      if (!selectedInspirationDesign && !selectedFabricTemplate && selectedTypes.length === 0) {
        showNotification("Please select at least one product type, an inspiration design, or a fabric template.", "error");
        return;
      }
    }

    // Fabric template flow requires fabric name, company logo, company name and at least
    // one garment design (when recommendations exist). Shade card stays optional.
    if (selectedFabricTemplate) {
      const effFabricName = (productDetails?.name || '').trim();
      const effCompanyName = (branding?.brandName || '').trim();
      const effLogo = branding?.logo;
      const hasGarmentOptions = fabricTemplateGarmentGroups.some((g) => (g?.items || []).length > 0);
      const missing = [];
      if (!effFabricName) missing.push('fabric name');
      if (!effCompanyName) missing.push('company name');
      if (!effLogo) missing.push('company logo');
      if (hasGarmentOptions && selectedFabricTemplateGarments.length === 0) missing.push('garment design');
      if (missing.length > 0) {
        setTemplateSetupAttempted(true);
        showNotification(`Please provide: ${missing.join(', ')}`, 'error');
        return;
      }
    }

    // Determine required credits based on fabric type, input method, and variants
    let requiredCredits;
    let variantsToProcess = [];
    
    // Check if we have multiple variants selected (from My Materials)
    if (productDetails.selectedVariants && productDetails.selectedVariants.length > 0) {
      variantsToProcess = productDetails.selectedVariants;
      console.log(`📸 Processing ${variantsToProcess.length} color variants`);
    } else {
      // Single image processing (existing logic)
      variantsToProcess = [{
        image: { s3Url: productDetails.image },
        colorName: 'Primary',
        isPrimary: true
      }];
    }
    
    // Credit logic update:
    // - We make ONE API call per product type
    // - Each call receives ALL color variants together (no per-variant calls)
    // - Therefore, image credits should be counted per product type only
    if (productInputMethod === 'types') {
      // One credit per selected product type (2x when 4K is enabled)
      requiredCredits = selectedTypes.length;
    } else {
      // Inspirational design or fabric template: a single catalog page / kit is generated.
      requiredCredits = 1;
    }

    // Check if user has enough image credits for all kits
    try {
      showNotification("Checking available credits...", "info");
      
      const { getUserProfile } = await import('../api');
      const token = localStorage.getItem('token');
      
      if (!token) {
        showNotification("Authentication required. Please log in again.", "error");
        return;
      }
      
      const response = await getUserProfile(token);
      
      if (response.data) {
        const userData = response.data.data.user;
        const availableCredits = userData.imageCredits || 0;
        const creditMultiplier = (userData.settings?.fourKEnabled ?? fourKEnabled) ? 2 : 1;
        requiredCredits *= creditMultiplier;
        
        console.log(`Credit check: Required ${requiredCredits}, Available ${availableCredits}`);
        
        if (availableCredits < requiredCredits) {
          showInsufficientCreditsModal(requiredCredits, availableCredits, 'image');
          return;
        }
        
        console.log(`✅ Credit check passed: ${availableCredits} credits available for ${requiredCredits} kits`);
      } else {
        console.error('No user data received from API');
        showNotification("Unable to verify credits. Please try again.", "error");
        return;
      }
    } catch (error) {
      console.error('Error checking user credits:', error);
      showNotification("Unable to verify credits. Please try again.", "error");
      return;
    }

    setIsMultipleKitGenerating(true);
    
    // Set progress based on input method and variants
    let totalKits;
    if (productInputMethod === 'types') {
      totalKits = selectedTypes.length * variantsToProcess.length; // Types × Variants
    } else {
      // Inspirational design or fabric template: 1 kit per variant
      totalKits = variantsToProcess.length;
    }
    setMultipleKitProgress({
      current: 0,
      total: totalKits,
      productType: '',
      status: 'pending',
      error: null
    });

    try {
      const { generateMultipleKits } = await import('../api/realtime.api');
      const sourceProductIdForKit = productDetails?._id ? String(productDetails._id) : undefined;
      const sourceInspirationalDesignIdForKit = (() => {
        const raw =
          (selectedInspirationDesign?.id || selectedInspirationDesign?._id
            ? String(selectedInspirationDesign.id || selectedInspirationDesign._id)
            : undefined) ||
          (productDetails?.inspirationalDesignId
            ? String(productDetails.inspirationalDesignId)
            : undefined);
        if (!raw || raw.startsWith('template-')) return undefined;
        return raw;
      })();

      const inspirationalDesignImageForLink =
        productDetails?.inspirationalDesign
          ? productDetails.inspirationalDesign.startsWith('http://') ||
            productDetails.inspirationalDesign.startsWith('https://')
            ? productDetails.inspirationalDesign
            : `https://fashdeck.com/${productDetails.inspirationalDesign.replace(/^\/+/, '')}`
          : null;

      let result;
      
      // Prepare the types array based on input method
      let typesToGenerate;
      if (productInputMethod === 'types') {
        typesToGenerate = selectedTypes;
      } else if (selectedFabricTemplate) {
        // Fabric template: ONE catalog page (one kit). The chosen garment designs (up to 3)
        // become the concepts featured inside that single template — they are passed through
        // advancedSettings (fabricTemplateGarments / conceptCount), not as separate kits.
        const chosenGarmentNames = selectedFabricTemplateGarments.map((g) => g.garment).filter(Boolean);
        const styleType =
          (chosenGarmentNames.length ? chosenGarmentNames.join(', ') : '') ||
          productDetails.inspirationalProductType?.trim() ||
          selectedFabricTemplate.productType ||
          selectedFabricTemplate.name ||
          productDetails.type?.trim() ||
          'Fabric Catalog';
        typesToGenerate = [{
          value: styleType,
          label: styleType,
          finalValue: styleType,
          finalLabel: styleType,
          category: selectedCategory?.value || productDetails?.category,
          categoryLabel: selectedCategory?.label,
        }];
      } else if (selectedInspirationDesign) {
        // For inspirational design, always use custom product type description (required field)
        const inspirationalType = productDetails.name?.trim() || selectedInspirationDesign.name;
        if (!inspirationalType) {
          throw new Error('Product type description is required for inspirational design mode');
        }
        
        typesToGenerate = [{ 
          value: inspirationalType, 
          label: inspirationalType, 
          finalValue: inspirationalType, 
          finalLabel: inspirationalType,
          category: selectedCategory?.value || productDetails?.category,
          categoryLabel: selectedCategory?.label
        }];
      } else {
        // Fallback to selected types if no inspiration design is selected
        typesToGenerate = selectedTypes;
      }

      // For a fabric template, the fabric name entered in step 3 (productDetails.name) is the
      // source of truth — it must win over the originally-uploaded product's name.
      const realFabricName = selectedFabricTemplate
        ? (productDetails?.name?.trim() ||
            productDetails?.selectedProduct?.name?.trim() ||
            '')
        : (productDetails?.selectedProduct?.name?.trim() ||
            productDetails?.name?.trim() ||
            '');
      const effectiveBranding = branding;
      // Chosen garment designs (with the audience each was picked from) for the single page.
      const effectiveTemplateGarments = selectedFabricTemplateGarments || [];
      const templateAdvancedOverrides = selectedFabricTemplate
        ? {
            ...(realFabricName ? { fabricProductName: realFabricName } : {}),
            ...(effectiveTemplateGarments.length
              ? {
                  fabricTemplateStyle: effectiveTemplateGarments.map((g) => g.garment).join(', '),
                  fabricTemplateGarments: effectiveTemplateGarments,
                  // One concept per chosen garment, inside a single catalog page.
                  conceptCount: effectiveTemplateGarments.length,
                }
              : {}),
          }
        : {};
      const realFabricDescription =
        productDetails?.selectedProduct?.description?.trim() ||
        productDetails?.description?.trim() ||
        '';
      const kitDisplayName =
        realFabricName ||
        (selectedFabricTemplate ? 'Fabric Catalog' : null) ||
        (selectedInspirationDesign ? (productDetails.name?.trim() || selectedInspirationDesign.name) : null) ||
        'Multiple Kits';

      const kidAgeForGeneration =
        productDetails.category === 'kids-boy' || productDetails.category === 'kids-girl'
          ? (productDetails.kidAge || selectedKidAge)
          : undefined;
      
      if (productDetails.modelImage) {
        // Virtual try-on mode: use V2 endpoint when a user/model image is provided.
        const { performVirtualTryOnV2 } = await import('../api/virtualTryon.api');
        const primaryImageUrl = variantsToProcess[0]?.image?.s3Url || variantsToProcess[0]?.image?.url;
        const vtonResults = [];
        const vtonErrors = [];

        setMultipleKitProgress({
          current: 0,
          total: typesToGenerate.length,
          productType: '',
          status: 'pending',
          error: null
        });

        for (let i = 0; i < typesToGenerate.length; i++) {
          const currentType = typesToGenerate[i];
          const typeLabel = currentType.finalLabel || currentType.label || currentType.value || `Type ${i + 1}`;
          const typeValue = currentType.finalValue || currentType.value || typeLabel;
          const typeCategory = currentType.category || productDetails.category;

          setMultipleKitProgress({
            current: i,
            total: typesToGenerate.length,
            productType: typeLabel,
            status: 'generating',
            error: null
          });

          try {
            const vtonResponse = await performVirtualTryOnV2(
              typeCategory,
              typeValue,
              primaryImageUrl,
              productDetails.modelImage,
              {
                ...(sourceInspirationalDesignIdForKit && {
                  sourceInspirationalDesignId: sourceInspirationalDesignIdForKit
                }),
                ...(inspirationalDesignImageForLink && {
                  inspirationalDesignImage: inspirationalDesignImageForLink
                }),
                ...(currentType.customization && {
                  productTypeCustomizations: currentType.customization
                }),
                ...(currentType.customizationReferences && {
                  productTypeCustomizationReferences: currentType.customizationReferences
                }),
                ...(currentType.garmentInspirationImage && {
                  garmentInspirationImage: currentType.garmentInspirationImage
                })
              }
            );

            if (vtonResponse?.success) {
              vtonResults.push({
                success: true,
                productType: typeLabel,
                data: vtonResponse.data
              });
              setMultipleKitProgress({
                current: i + 1,
                total: typesToGenerate.length,
                productType: typeLabel,
                status: 'completed',
                error: null
              });
            } else {
              const errMsg = vtonResponse?.message || 'Virtual try-on generation failed';
              vtonErrors.push({ productType: typeLabel, error: errMsg, success: false });
              setMultipleKitProgress({
                current: i + 1,
                total: typesToGenerate.length,
                productType: typeLabel,
                status: 'error',
                error: errMsg
              });
            }
          } catch (err) {
            const errMsg = err?.response?.data?.message || err?.message || 'Virtual try-on generation failed';
            vtonErrors.push({ productType: typeLabel, error: errMsg, success: false });
            setMultipleKitProgress({
              current: i + 1,
              total: typesToGenerate.length,
              productType: typeLabel,
              status: 'error',
              error: errMsg
            });
          }
        }

        result = {
          success: vtonResults.length > 0,
          totalGenerated: vtonResults.length,
          totalErrors: vtonErrors.length,
          processingTime: 0,
          kits: vtonResults,
          errors: vtonErrors,
          message: `Virtual try-on completed for ${vtonResults.length} type(s)${vtonErrors.length ? `, ${vtonErrors.length} failed` : ''}.`
        };
      } else if (shotType === 'both') {
        // Optimized approach: Make two API calls total (one for closeup, one for zoomout) with all variants
        console.log(`Generating kits with both close-up and zoom-out shots for ${variantsToProcess.length} variants in optimized calls...`);
        
        // Prepare all color variants for single API calls
        const allColorVariants = variantsToProcess.map(variant => ({
          colorName: variant.colorName,
          hex: variant.hex,
          image: variant.image?.s3Url || variant.image?.url,
          selected: true
        }));
        
        console.log(`🎨 Processing ${allColorVariants.length} color variants in optimized calls:`, allColorVariants.map(v => v.colorName));
        
        // Use the primary image for the main product image
        const primaryImageUrl = variantsToProcess[0]?.image?.s3Url || variantsToProcess[0]?.image?.url;
        
        // First API call for close-up shots with all variants
        console.log('📸 Generating close-up shots for all variants...');
        const closeupResult = await generateMultipleKits(
          primaryImageUrl,
          productDetails.category,
          false, // isStitched parameter (always false now)
          typesToGenerate,
          productDetails.modelImage || null, // userImageUrl
          {
            kitName: `${productInputMethod === 'types' ? 'Multiple Kits' : kitDisplayName} - Close-up - ${new Date().toLocaleDateString()}`,
            productName: realFabricName,
            productDescription: realFabricDescription || productDetails.description || 'Generated content for multiple product types',
            targetAudience: productDetails.targetAudience || 'General Audience',
            brandProfile: effectiveBranding,
            inspirationalDesignImage: productDetails.inspirationalDesign ? (
              productDetails.inspirationalDesign.startsWith('http://') || productDetails.inspirationalDesign.startsWith('https://') 
                ? productDetails.inspirationalDesign 
                : `https://fashdeck.com/${productDetails.inspirationalDesign.replace(/^\/+/, '')}`
            ) : null,
            advancedSettings: buildGenerationAdvancedSettings({ enableCloseupshot: true, ...templateAdvancedOverrides }),
            ...(sourceProductIdForKit && { sourceProductId: sourceProductIdForKit }),
            ...(sourceInspirationalDesignIdForKit && { sourceInspirationalDesignId: sourceInspirationalDesignIdForKit }),
            ...(kidAgeForGeneration && { kidAge: kidAgeForGeneration }),
          },
          true, // useGPTModel
          (progress) => {
            setMultipleKitProgress(progress);
          },
          allColorVariants // Pass all color variants in single call
        );
        
        // Second API call for zoom-out shots with all variants
        console.log('🔍 Generating zoom-out shots for all variants...');
        const zoomoutResult = await generateMultipleKits(
          primaryImageUrl,
          productDetails.category,
          false, // isStitched parameter (always false now)
          typesToGenerate,
          productDetails.modelImage || null, // userImageUrl
          {
            kitName: `${productInputMethod === 'types' ? 'Multiple Kits' : kitDisplayName} - Zoom-out - ${new Date().toLocaleDateString()}`,
            productName: realFabricName,
            productDescription: realFabricDescription || productDetails.description || 'Generated content for multiple product types',
            targetAudience: productDetails.targetAudience || 'General Audience',
            brandProfile: effectiveBranding,
            inspirationalDesignImage: productDetails.inspirationalDesign ? (
              productDetails.inspirationalDesign.startsWith('http://') || productDetails.inspirationalDesign.startsWith('https://') 
                ? productDetails.inspirationalDesign 
                : `https://fashdeck.com/${productDetails.inspirationalDesign.replace(/^\/+/, '')}`
            ) : null,
            advancedSettings: buildGenerationAdvancedSettings({ enableCloseupshot: false, ...templateAdvancedOverrides }),
            ...(sourceProductIdForKit && { sourceProductId: sourceProductIdForKit }),
            ...(sourceInspirationalDesignIdForKit && { sourceInspirationalDesignId: sourceInspirationalDesignIdForKit }),
            ...(kidAgeForGeneration && { kidAge: kidAgeForGeneration }),
          },
          true, // useGPTModel
          (progress) => {
            setMultipleKitProgress(progress);
          },
          allColorVariants // Pass all color variants in single call
        );
        
        // Combine results from both calls
        const combinedKits = [];
        let totalGenerated = 0;
        let totalErrors = 0;
        let totalProcessingTime = 0;
        let allSuccessful = true;
        
        if (closeupResult.success && zoomoutResult.success) {
          combinedKits.push(...(closeupResult.kits || []));
          combinedKits.push(...(zoomoutResult.kits || []));
          totalGenerated += (closeupResult.totalGenerated || 0) + (zoomoutResult.totalGenerated || 0);
          totalErrors += (closeupResult.totalErrors || 0) + (zoomoutResult.totalErrors || 0);
          totalProcessingTime += (closeupResult.processingTime || 0) + (zoomoutResult.processingTime || 0);
        } else {
          allSuccessful = false;
        }
        
        result = {
          success: allSuccessful,
          totalGenerated: totalGenerated,
          totalErrors: totalErrors,
          processingTime: totalProcessingTime,
          kits: combinedKits,
          message: `Successfully generated ${totalGenerated} kits with both close-up and zoom-out shots for ${allColorVariants.length} color variants in optimized calls!`
        };
        
        console.log('Optimized combined kit generation result:', result);
      } else {
        // Single API call for all variants - optimized approach
        console.log(`Generating kits with ${shotType} shots for ${variantsToProcess.length} variants in a single call...`);
        
        // Prepare all color variants for single API call
        const allColorVariants = variantsToProcess.map(variant => ({
          colorName: variant.colorName,
          hex: variant.hex,
          image: variant.image?.s3Url || variant.image?.url,
          selected: true
        }));
        
        console.log(`🎨 Processing ${allColorVariants.length} color variants in single API call:`, allColorVariants.map(v => v.colorName));
        
        const advancedSettings = buildGenerationAdvancedSettings({
          ...templateAdvancedOverrides,
        });
        
        // Use the primary image for the main product image
        const primaryImageUrl = variantsToProcess[0]?.image?.s3Url || variantsToProcess[0]?.image?.url;
        
        // Single API call with all color variants
        result = await generateMultipleKits(
          primaryImageUrl,
          productDetails.category,
          false, // isStitched parameter (always false now)
          typesToGenerate,
          productDetails.modelImage || null, // userImageUrl
          {
            kitName: `${productInputMethod === 'types' ? 'Multiple Kits' : kitDisplayName} - ${shotType} - ${new Date().toLocaleDateString()}`,
            productName: realFabricName,
            productDescription: realFabricDescription || productDetails.description || 'Generated content for multiple product types',
            targetAudience: productDetails.targetAudience || 'General Audience',
            brandProfile: effectiveBranding,
            inspirationalDesignImage: productDetails.inspirationalDesign ? (
              productDetails.inspirationalDesign.startsWith('http://') || productDetails.inspirationalDesign.startsWith('https://') 
                ? productDetails.inspirationalDesign 
                : `https://fashdeck.com/${productDetails.inspirationalDesign.replace(/^\/+/, '')}`
            ) : null,
            advancedSettings: advancedSettings,
            ...(sourceProductIdForKit && { sourceProductId: sourceProductIdForKit }),
            ...(sourceInspirationalDesignIdForKit && { sourceInspirationalDesignId: sourceInspirationalDesignIdForKit }),
            ...(kidAgeForGeneration && { kidAge: kidAgeForGeneration }),
          },
          true, // useGPTModel
          (progress) => {
            setMultipleKitProgress(progress);
          },
          allColorVariants // Pass all color variants in single call
        );
        
        // Update the result message to reflect the optimization
        if (result.success) {
          result.message = `Successfully generated ${result.totalGenerated || 0} kits with ${shotType} shots for ${allColorVariants.length} color variants in a single optimized call!`;
        }
      }

      console.log('Multiple kit generation result:', result);
      
      // Show success notification and redirect
      if (result.success) {
        const processingTime = result.processingTime ? Math.round(result.processingTime / 1000) : 0;
        showNotification(`Successfully generated ${result.totalGenerated} kits in ${processingTime}s!`, "success");
        
        // Refresh kits and open the latest kit in detail modal
        setTimeout(async () => {
          setActiveView('mykits');
          
          // Refresh the kits list to get the latest data
          if (refreshKits) {
            try {
              await refreshKits();
              
              // Wait a bit more for the refresh to complete, then open the latest kit
              setTimeout(async () => {
                try {
                  // Get fresh kits data directly from API to ensure we have the latest
                  const { getKits } = await import('../api/realtime.api');
                  const token = localStorage.getItem('token');
                  const kitsResponse = await getKits(token);
                  const freshKits = kitsResponse.data.data.kits;
                  
                  console.log('🔄 Fresh kits data:', freshKits);
                  console.log('🔄 Number of kits:', freshKits.length);
                  
                  if (viewKitDetails && freshKits && freshKits.length > 0) {
                    // Find the most recently created kit by sorting by creation date
                    const sortedKits = [...freshKits].sort((a, b) => {
                      const dateA = new Date(a.createdAt || a.dateGenerated || 0);
                      const dateB = new Date(b.createdAt || b.dateGenerated || 0);
                      return dateB - dateA; // Sort in descending order (newest first)
                    });
                    
                    const latestKit = sortedKits[0];
                    console.log('🎯 Latest kit found:', latestKit);
                    
                    if (latestKit) {
                      console.log('🎯 Opening latest generated kit:', latestKit.name);
                      
                      // Find the group that contains this kit by matching the original image URL
                      const kitGroup = freshKits.filter(kit => {
                        // Check if kits have the same original image URL
                        const kitOriginalImage = kit.productDetails?.originalImage || kit.productDetails?.image;
                        const latestKitOriginalImage = latestKit.productDetails?.originalImage || latestKit.productDetails?.image;
                        const isMatch = kitOriginalImage === latestKitOriginalImage;
                        console.log('🔍 Comparing:', {
                          kitName: kit.name,
                          kitOriginalImage,
                          latestKitOriginalImage,
                          isMatch
                        });
                        return isMatch;
                      });
                      
                      console.log('🔍 Kit group found:', kitGroup.length, 'kits');
                      console.log('🔍 Group kits:', kitGroup.map(k => k.name));
                      
                      if (kitGroup.length > 1) {
                        // Create a group object similar to what ConsolidatedKitCard expects
                        const group = {
                          originalImageUrl: latestKit.productDetails?.originalImage || latestKit.productDetails?.image,
                          groupName: `Kits with same original image (${kitGroup.length})`,
                          kits: kitGroup
                        };
                        
                        console.log('🎯 Opening grouped kit card with', kitGroup.length, 'kits');
                        console.log('🎯 Group object:', group);
                        viewKitDetails(latestKit, group);
                        showNotification(`Opened grouped kit card with ${kitGroup.length} kits`, "success");
                      } else {
                        // Single kit, open normally
                        console.log('🎯 Opening single kit:', latestKit.name);
                        viewKitDetails(latestKit);
                        showNotification(`Opened latest kit: ${latestKit.name}`, "success");
                      }
                    } else {
                      console.log('❌ No latest kit found');
                      showNotification("Kits refreshed! Check your latest generated kit.", "info");
                    }
                  } else {
                    console.log('❌ No fresh kits data available');
                    showNotification("Kits refreshed! Check your latest generated kit.", "info");
                  }
                } catch (error) {
                  console.error('❌ Error getting fresh kits data:', error);
                  showNotification("Kits refreshed! Check your latest generated kit.", "info");
                }
              }, 2000); // Increased wait time to ensure refresh is complete
            } catch (error) {
              console.error('❌ Error refreshing kits:', error);
              showNotification("Failed to refresh kits", "error");
            }
          }
        }, 1500);
      } else {
        const processingTime = result.processingTime ? Math.round(result.processingTime / 1000) : 0;
        showNotification(`Generated ${result.totalGenerated} kits with ${result.totalErrors} errors in ${processingTime}s.`, "warning");
        
        // Redirect to My Kits even if there were some errors
        setTimeout(() => {
          setActiveView('mykits');
        }, 1500);
      }

    } catch (error) {
      console.error('Error generating multiple kits:', error);
      setMultipleKitProgress(prev => ({
        ...prev,
        status: 'error',
        error: error.message
      }));
    } finally {
      setIsMultipleKitGenerating(false);
    }
  };

  // Reset shade cards + extracted colors when changing the base fabric.
  function resetShadeCardSelection() {
    colorVariantsSyncFingerprintRef.current = '';
    setFabricShadeCards([]);
    setExtractedColorVariants([]);
    setShowExtractionSuccess(false);
    setIsExtractingColors(false);
    setProductDetails(prev => ({
      ...prev,
      fabricShadeCards: [],
      shadeCards: [],
      shadeCard: null,
      colorVariants: [],
    }));
    if (shadeCardInputRef.current) {
      shadeCardInputRef.current.value = '';
    }
  }

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      resetShadeCardSelection();
      setDefaultFabricDraft(null);
      handleProductImageChange(file);
    }
  };

  const handleImageClick = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e) => {
      if (e.target.files[0]) {
        resetShadeCardSelection();
        setDefaultFabricDraft(null);
        handleProductImageChange(e.target.files[0]);
      }
    };
    fileInput.click();
  };

  // Handle shade card upload
  const handleShadeCardUpload = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      showNotification?.('Please upload an image file', 'error');
      return;
    }

    setIsUploadingShadeCard(true);
    try {
      // Compress and upload shade card to S3
      const compressed = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.8 });
      const uploadResult = await uploadImageToS3(compressed, {
        folder: 'shadecards',
        fileName: `shadecard_${Date.now()}_${compressed.name}`
      });

      // Extract colors from shade card
      setIsExtractingColors(true);
      try {
        const apiRes = await extractColorVariants(uploadResult.url, 100); // Extract up to 100 colors
        const palette = apiRes.palette || apiRes.data?.palette || [];

        const newShadeCard = {
          url: uploadResult.url,
          preview: uploadResult.url,
          file: file,
          extractedColors: palette
        };

        setFabricShadeCards(prev => [...prev, newShadeCard]);
        
        // Merge new colors with existing ones (avoid duplicates by hex value)
        setExtractedColorVariants(prevVariants => {
          const existingHexes = new Set(prevVariants.map(v => (v.hex || '').toLowerCase()));
          const newColors = Array.isArray(palette) ? palette
            .filter(c => !existingHexes.has((c.hex || '').toLowerCase()))
            .map((c, idx) => ({
              colorName: c.name || `Color ${prevVariants.length + idx + 1}`,
              hex: c.hex || '#000000'
            })) : [];
          return [...prevVariants, ...newColors];
        });
        
        // Update productDetails
        const updatedFabricShadeCards = [...(productDetails.fabricShadeCards || []), newShadeCard];
        const mergedColorVariants = (() => {
          const existingHexes = new Set((extractedColorVariants || []).map(v => (v.hex || '').toLowerCase()));
          const newColors = Array.isArray(palette) ? palette
            .filter(c => !existingHexes.has((c.hex || '').toLowerCase()))
            .map((c, idx) => ({
              colorName: c.name || `Color ${(extractedColorVariants?.length || 0) + idx + 1}`,
              hex: c.hex || '#000000'
            })) : [];
          return [...(extractedColorVariants || []), ...newColors];
        })();

        setProductDetails(prev => ({
          ...prev,
          fabricShadeCards: updatedFabricShadeCards
        }));

        // Immediately update fabric in backend when shade card is uploaded (don't wait for useEffect debounce)
        if (productDetails.savedProductId) {
          try {
            const { updateProduct } = await import('../api/product.api');
            const shadeCardsData = updatedFabricShadeCards.map(sc => ({ url: sc.url || sc }));
            await updateProduct(productDetails.savedProductId, {
              shadeCards: shadeCardsData,
              shadeCard: shadeCardsData.length > 0 ? { url: shadeCardsData[0].url } : null,
              colorVariants: mergedColorVariants.map(v => ({ colorName: v.colorName, hex: v.hex }))
            });
            console.log('✅ Shade card and extracted colors saved to fabric');
          } catch (updateErr) {
            console.error('Error saving shade card to fabric:', updateErr);
          }
        }

        // Show success message with color count
        if (palette.length > 0) {
          console.log(`✅ Successfully extracted ${palette.length} colors from shade card`);
          showNotification?.(`Shade card uploaded and ${palette.length} colors extracted successfully`, 'success');
          setShowExtractionSuccess(true);
          setTimeout(() => setShowExtractionSuccess(false), 3000);
        } else {
          showNotification?.('Shade card uploaded successfully', 'success');
        }
      } catch (extractError) {
        console.error('Error extracting colors from shade card:', extractError);
        // Still save the shade card even if color extraction fails
        const newShadeCard = {
          url: uploadResult.url,
          preview: uploadResult.url,
          file: file,
          extractedColors: []
        };

        const updatedFabricShadeCards = [...(productDetails.fabricShadeCards || []), newShadeCard];
        setFabricShadeCards(prev => [...prev, newShadeCard]);
        setProductDetails(prev => ({
          ...prev,
          fabricShadeCards: updatedFabricShadeCards
        }));

        // Immediately update fabric in backend with shade card (colors empty)
        if (productDetails.savedProductId) {
          try {
            const { updateProduct } = await import('../api/product.api');
            const shadeCardsData = updatedFabricShadeCards.map(sc => ({ url: sc.url || sc }));
            await updateProduct(productDetails.savedProductId, {
              shadeCards: shadeCardsData,
              shadeCard: shadeCardsData.length > 0 ? { url: shadeCardsData[0].url } : null,
              colorVariants: productDetails.colorVariants || []
            });
            console.log('✅ Shade card saved to fabric (color extraction failed)');
          } catch (updateErr) {
            console.error('Error saving shade card to fabric:', updateErr);
          }
        }

        showNotification?.('Shade card uploaded (color extraction failed)', 'warning');
      } finally {
        setIsExtractingColors(false);
      }
    } catch (error) {
      console.error('Error uploading shade card:', error);
      showNotification?.('Failed to upload shade card', 'error');
    } finally {
      setIsUploadingShadeCard(false);
      if (shadeCardInputRef.current) {
        shadeCardInputRef.current.value = '';
      }
    }
  };

  const handleShadeCardChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      await handleShadeCardUpload(file);
    }
  };

  const handleRemoveShadeCard = (index) => {
    setFabricShadeCards(prev => prev.filter((_, i) => i !== index));
    setProductDetails(prev => ({
      ...prev,
      fabricShadeCards: (prev.fabricShadeCards || []).filter((_, i) => i !== index)
    }));
  };

  // Update productDetails when fabric properties change (guard to prevent unnecessary updates)
  useEffect(() => {
    setProductDetails(prev => {
      const newTexture = selectedTexture ? { name: selectedTexture.name, description: selectedTexture.description, categoryName: selectedTexture.categoryName } : null;
      const textureChanged = (prev.fabricTexture?.name !== newTexture?.name) || (prev.fabricTexture?.categoryName !== newTexture?.categoryName);
      const weightChanged = prev.fabricWeight !== fabricWeight;
      if (!weightChanged && !textureChanged) return prev;
      return { ...prev, fabricWeight: fabricWeight, fabricTexture: newTexture };
    });
  }, [fabricWeight, selectedTexture?.name, selectedTexture?.categoryName]);

  // Stable fingerprint for saved-product shade cards (parent often passes a new array reference each render)
  const fabricShadeCardsUrlsKey = useMemo(() => {
    const cards = productDetails?.fabricShadeCards;
    if (!cards?.length) return '';
    return cards.map((sc) => (typeof sc === 'string' ? sc : sc?.url || '')).join('|');
  }, [productDetails?.fabricShadeCards]);

  const fabricShadeCardsInitKey = useMemo(() => {
    const id = productDetails?.savedProductId;
    if (!id || !fabricShadeCardsUrlsKey) return '';
    return `${id}:${fabricShadeCardsUrlsKey}`;
  }, [productDetails?.savedProductId, fabricShadeCardsUrlsKey]);

  // Initialize fabricShadeCards when product is loaded from My Materials (has existing shade cards)
  useEffect(() => {
    if (!fabricShadeCardsInitKey || fabricShadeCards.length > 0) return;
    const cards = productDetails.fabricShadeCards;
    const existingShadeCards = cards.map((sc) => ({
      url: typeof sc === 'string' ? sc : sc.url,
      preview: typeof sc === 'string' ? sc : sc.url,
      extractedColors: typeof sc === 'object' && sc.extractedColors ? sc.extractedColors : []
    }));
    setFabricShadeCards(existingShadeCards);
  }, [fabricShadeCardsInitKey, fabricShadeCards.length]);

  // Content fingerprints so this effect does not re-run on new array/object references from parent (same data).
  const fabricShadeCardsAggKey = JSON.stringify(
    (fabricShadeCards || []).map((c) => ({
      u: c.url,
      e: (c.extractedColors || []).map((x) => [(x.hex || '').toLowerCase(), x.name || ''])
    }))
  );
  // Aggregate shade-card colors and sync to productDetails in one guarded pass (avoids sync ↔ aggregate loop).
  useEffect(() => {
    const aggregated = fabricShadeCards.length
      ? aggregateColorVariantsForFabric(fabricShadeCards, productDetails.colorVariants)
      : [];
    const nextKey = colorVariantsFingerprint(aggregated);
    if (nextKey === colorVariantsSyncFingerprintRef.current) return;
    colorVariantsSyncFingerprintRef.current = nextKey;

    setExtractedColorVariants((prev) => (colorVariantsEqual(prev, aggregated) ? prev : aggregated));
    setProductDetails((prev) =>
      colorVariantsEqual(prev.colorVariants, aggregated) ? prev : { ...prev, colorVariants: aggregated }
    );
  }, [fabricShadeCardsAggKey]);

  // Update saved product in backend when fabric properties change (including shade cards and extracted colors)
  useEffect(() => {
    const updateProductInBackend = async () => {
      // Only update if product was already saved and we have fabric properties
      if (!productDetails.savedProductId) return;
      
      // Only update if at least one fabric property is set
      if (!productDetails.fabricTexture && !productDetails.fabricWeight && (!productDetails.fabricShadeCards || productDetails.fabricShadeCards.length === 0) && extractedColorVariants.length === 0) {
        return;
      }

      try {
        const { updateProduct } = await import('../api/product.api');
        
        // Prepare shade cards data - include url (extractedColors stored in colorVariants)
        const shadeCardsData = productDetails.fabricShadeCards?.length > 0
          ? productDetails.fabricShadeCards.map(sc => ({ url: sc.url || sc }))
          : null;
        
        // Prepare fabric texture data
        const fabricTextureData = productDetails.fabricTexture
          ? {
              name: productDetails.fabricTexture.name,
              description: productDetails.fabricTexture.description,
              categoryName: productDetails.fabricTexture.categoryName
            }
          : null;
        
        // Prepare color variants from extracted colors (format: { colorName, hex })
        const colorVariantsData = extractedColorVariants.length > 0
          ? extractedColorVariants.map(v => ({ colorName: v.colorName, hex: v.hex }))
          : [];
        
        const updateData = {
          fabricTexture: fabricTextureData,
          fabricWeight: productDetails.fabricWeight || null,
          shadeCards: shadeCardsData,
          // For backward compatibility
          shadeCard: shadeCardsData && shadeCardsData.length > 0 ? { url: shadeCardsData[0].url } : null,
          colorVariants: colorVariantsData
        };

        await updateProduct(productDetails.savedProductId, updateData);
        console.log('✅ Product fabric properties (shade cards + extracted colors) updated successfully');
      } catch (error) {
        console.error('Error updating product fabric properties:', error);
        // Silent failure - don't interrupt user flow
      }
    };

    // Debounce the update to avoid too many API calls
    const timeoutId = setTimeout(() => {
      updateProductInBackend();
    }, 1000); // Wait 1 second after last change

    return () => clearTimeout(timeoutId);
  }, [productDetails.savedProductId, productDetails.fabricTexture, productDetails.fabricWeight, productDetails.fabricShadeCards, extractedColorVariants]);

  // Map a fabric/material string to a known texture from FABRIC_TEXTURES
  const mapMaterialToTexture = (materialString) => {
    if (!materialString || typeof materialString !== 'string') return null;
    const text = materialString.toLowerCase();

    // Simple keyword → texture name mapping
    const keywordToTextureName = [
      ['satin', 'Satin', 'Smooth & Lustrous'],
      ['silk', 'Silk', 'Smooth & Lustrous'],
      ['poplin', 'Poplin', 'Smooth & Lustrous'],
      ['sateen', 'Sateen', 'Smooth & Lustrous'],
      ['chiffon', 'Chiffon', 'Smooth & Lustrous'],
      ['georgette', 'Georgette', 'Smooth & Lustrous'],
      ['crepe', 'Crepe', 'Smooth & Lustrous'],
      ['velvet', 'Velvet', 'Soft, Plush & Fuzzy'],
      ['velour', 'Velour', 'Soft, Plush & Fuzzy'],
      ['fleece', 'Fleece', 'Soft, Plush & Fuzzy'],
      ['linen', 'Linen', 'Crisp & Structured'],
      ['taffeta', 'Taffeta', 'Crisp & Structured'],
      ['organza', 'Organza', 'Crisp & Structured'],
      ['voile', 'Voile', 'Crisp & Structured'],
      ['denim', 'Denim', 'Natural & Rustic'],
      ['chambray', 'Chambray', 'Natural & Rustic'],
      ['jute', 'Jute', 'Natural & Rustic'],
      ['hemp', 'Hemp', 'Natural & Rustic'],
      ['burlap', 'Burlap', 'Natural & Rustic'],
      ['khadi', 'Khadi', 'Natural & Rustic'],
      ['jersey', 'Jersey', 'Knitted & Stretchy'],
      ['rib', 'Rib Knit', 'Knitted & Stretchy'],
      ['interlock', 'Interlock Knit', 'Knitted & Stretchy'],
      ['lace', 'Lace', 'Knitted & Stretchy'],
      ['corduroy', 'Corduroy', 'Rough & Textured'],
      ['tweed', 'Tweed', 'Rough & Textured'],
      ['herringbone', 'Herringbone', 'Rough & Textured'],
      ['houndstooth', 'Houndstooth', 'Rough & Textured']
    ];

    for (const [keyword, textureName, categoryName] of keywordToTextureName) {
      if (text.includes(keyword)) {
        // Find the texture object from FABRIC_TEXTURES for richer data
        const category = FABRIC_TEXTURES.fabricTextures.find(c => c.category === categoryName);
        const texture = category?.types.find(t => t.name === textureName);
        if (texture) {
          return { ...texture, categoryName };
        }
        // Fallback minimal object
        return { name: textureName, categoryName };
      }
    }
    return null;
  };

  // Auto-select texture when a product is chosen from My Materials (step 2).
  // Use primitive deps only — `selectedProduct` object identity changes whenever App merges productDetails.
  const selectedProductId = productDetails?.selectedProduct?._id;
  const selectedProductMaterialsRaw =
    productDetails?.selectedProduct?.specifications?.materials ||
    productDetails?.selectedProduct?.materials;
  const selectedProductMaterialsString =
    typeof selectedProductMaterialsRaw === 'string' ? selectedProductMaterialsRaw : '';

  useEffect(() => {
    if (selectedTexture) return;
    if (!selectedProductMaterialsString) return;
    const autoTexture = mapMaterialToTexture(selectedProductMaterialsString);
    if (!autoTexture) return;
    setSelectedTexture((prev) => {
      if (
        prev &&
        prev.name === autoTexture.name &&
        (prev.categoryName || '') === (autoTexture.categoryName || '')
      ) {
        return prev;
      }
      return autoTexture;
    });
  }, [selectedTexture, selectedProductId, selectedProductMaterialsString]);

  // Load recently uploaded fabrics (latest first)
  useEffect(() => {
    let mounted = true;
    const loadRecentFabrics = async () => {
      try {
        setLoadingRecentFabrics(true);
        const res = await getProducts(1, 8, '');
        const items = Array.isArray(res?.products) ? res.products : [];
        if (mounted) setRecentFabrics(items);
      } catch (err) {
        if (mounted) setRecentFabrics([]);
      } finally {
        if (mounted) setLoadingRecentFabrics(false);
      }
    };
    loadRecentFabrics();
    return () => { mounted = false; };
  }, []);

  const applySelectedProduct = (product) => {
    const chosenUrl = product.image || product.images?.original?.imgbbUrl || product.images?.original?.url || product.images?.original?.s3Url;
    if (!chosenUrl) return;

    setDefaultFabricDraft(null);
    setShowExtractionSuccess(false);
    setIsExtractingColors(false);
    if (shadeCardInputRef.current) {
      shadeCardInputRef.current.value = '';
    }

    // Hydrate shade cards + extracted colors when selecting from My Materials
    const rawShadeCards = (
      product.fabricShadeCards ||
      product.shadeCards ||
      product.shadecards ||
      (product.shadeCard ? [product.shadeCard] : []) ||
      []
    );
    const existingShadeCards = Array.isArray(rawShadeCards)
      ? rawShadeCards
          .map((sc) => ({
            url: typeof sc === 'string' ? sc : (sc?.url || sc?.s3Url || ''),
            preview: typeof sc === 'string' ? sc : (sc?.url || sc?.s3Url || ''),
            extractedColors: (typeof sc === 'object' && Array.isArray(sc?.extractedColors)) ? sc.extractedColors : []
          }))
          .filter((sc) => Boolean(sc.url))
      : [];

    const colorVariants = aggregateColorVariantsForFabric(
      existingShadeCards,
      product.colorVariants || []
    );
    colorVariantsSyncFingerprintRef.current = colorVariantsFingerprint(colorVariants);

    setFabricShadeCards(existingShadeCards);
    setExtractedColorVariants(colorVariants);

    const allVariants = [];
    // Add primary image as first variant
    allVariants.push({
      image: { s3Url: chosenUrl },
      colorName: 'Primary',
      isPrimary: true
    });

    // Add all color variants
    if (product.colorVariants && product.colorVariants.length > 0) {
      product.colorVariants.forEach((variant, index) => {
        allVariants.push({
          ...variant,
          isPrimary: false,
          originalIndex: index
        });
      });
    }

    setProductDetails(prev => ({
      ...prev,
      savedProductId: product?._id || product?.id || prev.savedProductId,
      image: chosenUrl,
      name: product.name || prev.name,
      description: product.description || prev.description,
      category: product.category || prev.category,
      selectedProduct: product,
      fabricAnalysis: product.fabricAnalysis || null,
      fabricWeight: product.fabricAnalysis?.fabric_analysis?.gsm_weight || product.fabricWeight || prev.fabricWeight || '',
      colorVariants,
      fabricShadeCards: existingShadeCards.map((sc) => ({ url: sc.url, extractedColors: sc.extractedColors })),
      shadeCards: existingShadeCards.map((sc) => ({ url: sc.url })),
      shadeCard: existingShadeCards[0] ? { url: existingShadeCards[0].url } : null,
      selectedVariants: allVariants,
      availableVariants: allVariants,
      selectedVariantIndex: 0
    }));
  };

  return (
    <div className="relative w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-6 lg:py-8 font-sans overflow-hidden min-h-screen md:h-[calc(100vh-68px)] md:min-h-[calc(100vh-68px)] flex flex-col">
      <img
        src={generateKitBackground}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-70"
      />
      <div className="pointer-events-none absolute inset-0 bg-white/25" />

      <div className="relative z-10 min-w-0 w-full bg-white/90 backdrop-blur-[1px] p-3 sm:p-5 lg:p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto pb-24">
          {/* Step Content */}
          {currentStep === 0 && (
          <div className="relative space-y-4 sm:space-y-6 min-w-0">
            {showFabricPipelineOverlay && (
              <div
                className="absolute inset-0 z-[60] flex items-center justify-center rounded-xl p-3 sm:p-6 min-h-[240px]"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <div className="absolute inset-0 rounded-xl bg-slate-900/35 backdrop-blur-sm" />
                <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/30 bg-white/95 p-5 sm:p-6 shadow-2xl shadow-indigo-500/15 ring-1 ring-indigo-100/90">
                  <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br from-indigo-400/35 to-fuchsia-400/25 blur-2xl" />
                  <div className="pointer-events-none absolute -bottom-6 -left-6 h-20 w-20 rounded-full bg-gradient-to-tr from-violet-400/30 to-transparent blur-xl" />

                  <div className="relative flex items-start gap-3 mb-5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-500/35">
                      <Sparkles size={20} className="animate-pulse" />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-slate-900 tracking-tight">Preparing your fabric</p>
                      <p className="text-sm text-slate-500 mt-0.5">Upscaling and analysis for best kit quality</p>
                    </div>
                  </div>

                  <ul className="relative space-y-0">
                    {FABRIC_PROCESS_STEPS.map((step, idx) => {
                      const isDone = idx < fabricPipelineActiveIndex;
                      const isActive = idx === fabricPipelineActiveIndex;
                      return (
                        <li
                          key={step.key}
                          className={`flex items-center gap-3 py-3 border-b border-slate-100 last:border-0 last:pb-0 ${
                            isActive ? 'text-slate-900' : isDone ? 'text-emerald-700' : 'text-slate-400'
                          }`}
                        >
                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all ${
                              isDone
                                ? 'bg-emerald-100 text-emerald-700'
                                : isActive
                                  ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-300 ring-offset-2'
                                  : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {isDone ? <Check size={16} strokeWidth={3} /> : idx + 1}
                          </span>
                          <span className={`text-sm flex-1 ${isActive ? 'font-semibold' : 'font-medium'}`}>
                            {step.label}
                          </span>
                          {isActive && (
                            <Loader2 size={18} className="shrink-0 animate-spin text-indigo-600" aria-hidden />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start min-w-0">
              {/* Left: Upload card */}
              <div className="min-w-0">
                <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Scissors size={18} className="text-indigo-600" />
                    <h2 className="text-sm sm:text-base font-semibold text-slate-900">Upload Your Fabric</h2>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 mb-4">
                    Transform your sketched fabric into stunning fashion UGCs
                  </p>

                  {!liteMode && (
                    <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4 order-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Layers size={16} className="text-indigo-600 shrink-0" />
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-900">Default fabrics</h3>
                      </div>
                      <p className="text-[11px] sm:text-xs text-slate-600 mb-3">
                        Pick a base fabric, choose a color, adjust type and weight, then create a swatch — no photo required.
                      </p>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                        {DEFAULT_FABRIC_PRESETS.map((preset) => {
                          const active = defaultFabricDraft?.presetId === preset.id;
                          return (
                            <button
                              key={preset.id}
                              type="button"
                              onClick={() => selectDefaultFabricPreset(preset)}
                              className={[
                                'rounded-lg border px-2 py-1.5 text-[11px] sm:text-xs font-medium transition touch-manipulation',
                                active
                                  ? 'border-indigo-500 bg-indigo-50 text-indigo-900 ring-1 ring-indigo-200'
                                  : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-white',
                              ].join(' ')}
                            >
                              {preset.label}
                            </button>
                          );
                        })}
                      </div>

                      {defaultFabricDraft ? (
                        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
                          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                            <div>
                              <label className="text-[11px] sm:text-xs text-slate-600 mb-1 block">Fabric color</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={(() => {
                                    const m = (defaultFabricDraft.hex || '').trim().match(/^#?([0-9a-fA-F]{6})$/i);
                                    return m ? `#${m[1]}` : '#5b6b8c';
                                  })()}
                                  onChange={(e) => updateDefaultFabricDraft({ hex: e.target.value })}
                                  className="h-10 w-14 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
                                  aria-label="Fabric color"
                                />
                                <input
                                  type="text"
                                  value={defaultFabricDraft.hex || ''}
                                  onChange={(e) => updateDefaultFabricDraft({ hex: e.target.value })}
                                  onBlur={() => {
                                    setDefaultFabricDraft((d) => {
                                      if (!d) return d;
                                      const raw = (d.hex || '').trim();
                                      const m = raw.match(/^#?([0-9a-fA-F]{6})$/i);
                                      if (m) return { ...d, hex: `#${m[1]}` };
                                      if (raw === '' || raw === '#') return { ...d, hex: '#5b6b8c' };
                                      return d;
                                    });
                                  }}
                                  className="flex-1 min-w-0 rounded-lg border border-slate-200 px-2 py-2 text-xs font-mono text-slate-900"
                                  placeholder="#5b6b8c"
                                  spellCheck={false}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] text-slate-600 mb-1 block">Fabric type</label>
                              <TextureSelector
                                selectedTexture={findTextureByFabricType(defaultFabricDraft.fabric_type)}
                                onTextureChange={(texture) =>
                                  updateDefaultFabricDraft({ fabric_type: texture ? texture.name : '' })
                                }
                                showTip={false}
                                onTipToggle={() => {}}
                                dropdownMode="inline"
                              />
                            </div>
                            <div>
                              <label className="text-[11px] text-slate-600 mb-1 block">Weave / knit</label>
                              <div className="relative">
                                <select
                                  value={defaultFabricDraft.weave_or_knit || ''}
                                  onChange={(e) => updateDefaultFabricDraft({ weave_or_knit: e.target.value })}
                                  className="w-full h-11 px-3 pr-9 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white appearance-none"
                                >
                                  <option value="">Select</option>
                                  {[...new Set([...(defaultFabricDraft.weave_or_knit && !WEAVE_OR_KNIT_OPTIONS.includes(defaultFabricDraft.weave_or_knit) ? [defaultFabricDraft.weave_or_knit] : []), ...WEAVE_OR_KNIT_OPTIONS])].map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={16} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              </div>
                            </div>
                            <div>
                              <label className="text-[11px] text-slate-600 mb-1 block">Opacity</label>
                              <div className="relative">
                                <select
                                  value={defaultFabricDraft.opacity || ''}
                                  onChange={(e) => updateDefaultFabricDraft({ opacity: e.target.value })}
                                  className="w-full h-11 px-3 pr-9 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white appearance-none"
                                >
                                  <option value="">Select</option>
                                  {[...new Set([...(defaultFabricDraft.opacity && !OPACITY_OPTIONS.includes(defaultFabricDraft.opacity) ? [defaultFabricDraft.opacity] : []), ...OPACITY_OPTIONS])].map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={16} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              </div>
                            </div>
                            <div>
                              <label className="text-[11px] text-slate-600 mb-1 block">Stretch</label>
                              <div className="relative">
                                <select
                                  value={defaultFabricDraft.stretch || ''}
                                  onChange={(e) => updateDefaultFabricDraft({ stretch: e.target.value })}
                                  className="w-full h-11 px-3 pr-9 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white appearance-none"
                                >
                                  <option value="">Select</option>
                                  {[...new Set([...(defaultFabricDraft.stretch && !STRETCH_OPTIONS.includes(defaultFabricDraft.stretch) ? [defaultFabricDraft.stretch] : []), ...STRETCH_OPTIONS])].map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={16} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              </div>
                            </div>
                            <div className="sm:col-span-2">
                              <label className="text-[11px] text-slate-600 mb-1 block">GSM weight</label>
                              <div className="relative">
                                <select
                                  value={defaultFabricDraft.gsm_weight || ''}
                                  onChange={(e) => updateDefaultFabricDraft({ gsm_weight: e.target.value })}
                                  className="w-full h-11 px-3 pr-9 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white appearance-none"
                                >
                                  <option value="">Select</option>
                                  {[...new Set([...(defaultFabricDraft.gsm_weight && !GSM_WEIGHT_OPTIONS.includes(defaultFabricDraft.gsm_weight) ? [defaultFabricDraft.gsm_weight] : []), ...GSM_WEIGHT_OPTIONS])].map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={16} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            disabled={defaultFabricApplying || productImageUploading || fabricAnalysisLoading}
                            onClick={() => applyDefaultFabricSwatch()}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:pointer-events-none"
                          >
                            {defaultFabricApplying ? (
                              <>
                                <Loader2 size={16} className="animate-spin shrink-0" />
                                Creating swatch…
                              </>
                            ) : (
                              <>
                                <PaletteIcon size={16} className="shrink-0" />
                                Create fabric from selection
                              </>
                            )}
                          </button>
                        </div>
                      ) : (
                        <p className="text-[11px] text-slate-500">Tap a fabric above to set color and properties.</p>
                      )}
                    </div>
                  )}

                  {/* Upload Image */}
                  <div
                    className={`border-2 border-dashed border-indigo-200 rounded-2xl p-5 sm:p-6 flex flex-col items-center mb-4 bg-indigo-50/30 cursor-pointer relative hover:border-indigo-300 hover:bg-indigo-50/60 transition touch-manipulation ${
                      liteMode ? 'order-1' : 'order-2'
                    }`}
                    onClick={handleImageClick}
                    onDrop={handleImageDrop}
                    onDragOver={e => e.preventDefault()}
                  >
                    {productDetails.image ? (
                      <div className="relative">
                        <BlobPreview
                          file={productDetails.image}
                          alt="Preview"
                          className="h-24 w-24 sm:h-28 sm:w-28 object-cover rounded-lg mb-3 shadow-md border border-slate-200"
                        />
                        {/* Variant indicator */}
                        {productDetails.selectedProduct && productDetails.colorVariants && productDetails.colorVariants.length > 0 && (
                          <div className="absolute -bottom-1 -left-1 bg-indigo-600 text-white text-xs px-2 py-1 rounded-full font-medium shadow-sm">
                            {productDetails.selectedVariantIndex === 0 ? 'Primary' :
                             productDetails.colorVariants[productDetails.selectedVariantIndex - 1]?.colorName || `Variant ${productDetails.selectedVariantIndex}`}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (showNotification) {
                              showNotification('Material removed. Please choose a new material.', 'info');
                            }
                            resetShadeCardSelection();
                            setDefaultFabricDraft(null);
                            setProductDetails(prev => ({
                              ...prev,
                              image: null,
                              selectedProduct: null,
                              colorVariants: [],
                              selectedVariants: [],
                              availableVariants: [],
                              selectedVariantIndex: 0
                            }));
                          }}
                          className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold hover:bg-rose-600 transition-colors shadow-md"
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center w-full">
                        <div className="flex items-center justify-center mb-3">
                          <div className="relative">
                            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-2xl flex items-center justify-center border border-slate-200 shadow-sm">
                              <ImageIcon size={32} className="text-slate-400" />
                            </div>
                            <div className="absolute -top-1 -right-1 w-7 h-7 bg-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                              <Plus size={14} className="text-white" />
                            </div>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-semibold text-indigo-700">Transform your sketched fabric into stunning fashion UGCs</div>
                          {!liteMode && (
                            <div className="text-xs text-slate-500 mt-1">Click to upload or drag and drop</div>
                          )}
                        </div>
                        <div className="w-full space-y-2 mt-4">
                          {!liteMode && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.accept = 'image/*';
                                  input.onchange = (event) => {
                                    if (event.target.files && event.target.files[0]) {
                                      resetShadeCardSelection();
                                      setDefaultFabricDraft(null);
                                      handleProductImageChange(event.target.files[0]);
                                    }
                                  };
                                  input.click();
                                }}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-medium text-sm shadow-sm hover:bg-indigo-700 hover:shadow-md transition touch-manipulation"
                              >
                                <UploadCloud size={16} className="shrink-0" />
                                <span className="truncate">Upload Image</span>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCamera('fabric', 'environment');
                                }}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-lg font-medium text-sm shadow-sm hover:bg-slate-900 hover:shadow-md transition touch-manipulation"
                              >
                                <Camera size={16} className="shrink-0" />
                                <span className="truncate">Take Photo</span>
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setIsProductModalOpen(true); }}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-slate-700 border border-slate-300 rounded-lg font-medium text-sm hover:bg-slate-50 hover:border-slate-400 transition touch-manipulation"
                          >
                            <ShoppingBag size={16} className="shrink-0" />
                            <span className="truncate">Choose from My Materials</span>
                          </button>
                        </div>
                      </div>
                    )}
                    {productImageUploading && !showFabricPipelineOverlay && (
                      <div className="flex items-center space-x-2 text-sm text-indigo-600 mt-3">
                        <Loader2 size={16} className="animate-spin" />
                        <span>Uploading...</span>
                      </div>
                    )}
                  </div>

                  {/* Recently Uploaded Fabrics (only when no fabric is uploaded/selected) */}
                  {!productDetails?.selectedProduct && !productDetails?.image && (
                    <div className="mb-3 sm:mb-4 min-w-0 order-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-slate-800">Recently uploaded fabrics</h3>
                        <button
                          type="button"
                          onClick={() => setIsProductModalOpen(true)}
                          className="text-xs text-indigo-600 hover:underline"
                        >
                          View all
                        </button>
                      </div>
                      {loadingRecentFabrics ? (
                        <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                          <Loader2 size={14} className="animate-spin" />
                          Loading recent fabrics...
                        </div>
                      ) : recentFabrics.length > 0 ? (
                        <div className="w-full py-1">
                          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 sm:gap-3">
                            {recentFabrics.map((fabric) => {
                              const img = fabric.image || fabric.images?.original?.imgbbUrl || fabric.images?.original?.url || fabric.images?.original?.s3Url;
                              if (!img) return null;
                              const thumbnailImg = toThumbnailUrl(img);
                              const isSelected = String(productDetails?.selectedProduct?._id || '') === String(fabric._id || '');
                              return (
                                <button
                                  key={fabric._id}
                                  type="button"
                                  onClick={() => applySelectedProduct(fabric)}
                                  className={`w-full ${isSelected ? 'ring-2 ring-indigo-500 rounded-lg' : ''}`}
                                  title={fabric.name || 'Fabric'}
                                >
                                  <img
                                    src={thumbnailImg}
                                    alt={fabric.name || 'Fabric'}
                                    className="w-full aspect-square object-cover rounded-lg border"
                                    onError={(e) => {
                                      if (e.currentTarget.src !== img) {
                                        e.currentTarget.src = img;
                                      }
                                    }}
                                  />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500">No recently uploaded fabrics found.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Keep Shade Cards + extracted colors in left column (below upload) */}

              {/* All Color Variants Selected - Show when product with variants is selected */}
              {productDetails.selectedProduct &&
                productDetails.selectedVariants &&
                productDetails.selectedVariants.filter(v => !(v.isPrimary || (v.colorName && v.colorName.trim().toLowerCase() === 'primary'))).length > 0 && (
                <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Palette size={16} className="text-emerald-600" />
                    <span className="text-sm font-medium text-slate-900">
                      {productDetails.selectedVariants.filter(v => !(v.isPrimary || (v.colorName && v.colorName.trim().toLowerCase() === 'primary'))).length} Color Variants
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">
                    {productDetails.selectedVariants.filter(v => !(v.isPrimary || (v.colorName && v.colorName.trim().toLowerCase() === 'primary'))).length > 0 
                      ? `${productDetails.selectedVariants.filter(v => !(v.isPrimary || (v.colorName && v.colorName.trim().toLowerCase() === 'primary'))).length} color variant${productDetails.selectedVariants.filter(v => !(v.isPrimary || (v.colorName && v.colorName.trim().toLowerCase() === 'primary'))).length > 1 ? 's' : ''} available for your kit.`
                      : 'No color variants available. Kit generation will use the primary image only.'}
                  </p>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {productDetails.selectedVariants
                      .filter(v => !(v.isPrimary || (v.colorName && v.colorName.trim().toLowerCase() === 'primary')))
                      .map((variant, index) => (
                      <div
                        key={index}
                        className="relative border-2 rounded-lg p-2 border-emerald-200 bg-emerald-50"
                      >
                        {/* Color Palette Display */}
                        <div className="w-full h-20 rounded flex items-center justify-center relative overflow-hidden">
                          {variant.hex ? (
                            <div
                              className="w-full h-full rounded"
                              style={{ backgroundColor: variant.hex }}
                            />
                          ) : variant.image?.s3Url || variant.image?.url ? (
                            <img
                              src={variant.image?.s3Url || variant.image?.url}
                              alt={variant.colorName || `Variant ${index + 1}`}
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                              <span className="text-gray-500 text-xs">No Color</span>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-1 text-center truncate font-medium">
                          {variant.colorName || `Variant ${index + 1}`}
                        </div>
                        {variant.hex && (
                          <div className="text-xs text-gray-500 text-center">
                            {variant.hex}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-3 p-2 bg-emerald-100 rounded-lg">
                    <p className="text-xs text-emerald-700 text-center">
                      <span className="font-medium">Kit will include:</span> {productDetails.selectedVariants.length} different color variation{productDetails.selectedVariants.length !== 1 ? 's' : ''} of your product
                    </p>
                  </div>
                  
                  {/* Show available variants that can be reselected */}
                  {productDetails.availableVariants && productDetails.availableVariants.length > productDetails.selectedVariants.length && (
                    <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-slate-600 font-medium">Available variants to add back:</span>
                        <div className="bg-slate-200 text-slate-600 px-2 py-1 rounded-full text-xs font-medium">
                          {productDetails.availableVariants.length - productDetails.selectedVariants.length} available
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {productDetails.availableVariants
                          .filter(variant => !productDetails.selectedVariants.some(selected => 
                            selected.colorName === variant.colorName && selected.isPrimary === variant.isPrimary
                          ))
                          .map((variant, index) => (
                            <div
                              key={index}
                              onClick={() => handleVariantReselect(variant)}
                              className="group relative border-2 rounded-lg p-2 cursor-pointer transition-all duration-200 hover:scale-105 border-slate-300 bg-white hover:border-emerald-400"
                            >
                              <img
                                src={variant.image?.s3Url || variant.image?.url || 'https://via.placeholder.com/80x80?text=No+Image'}
                                alt={variant.colorName || `Variant ${index + 1}`}
                                className="w-full h-16 object-cover rounded"
                              />
                              <div className="text-xs text-gray-600 mt-1 text-center truncate font-medium">
                                {variant.colorName || `Variant ${index + 1}`}
                              </div>
                              <div className="absolute inset-0 bg-emerald-500 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                                <span className="text-emerald-600 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white px-2 py-1 rounded shadow-sm">
                                  ✓ Add to Kit
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Shade Cards Section - Show when fabric image is uploaded */}
              {!liteMode && productDetails.image && (
                <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="space-y-4">
                    {/* Shade Cards */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Shade Cards
                        </label>
                        {fabricShadeCards.length > 0 && (
                          <span className="text-xs text-slate-600 bg-slate-200 px-2 py-1 rounded-full">
                            {fabricShadeCards.length} card{fabricShadeCards.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      
                      {/* Existing Shade Cards */}
                      {fabricShadeCards.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                          {fabricShadeCards.map((shadeCard, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={shadeCard.preview || shadeCard.url}
                                alt={`Shade card ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border border-slate-200"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveShadeCard(index)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600 transition-colors shadow-md opacity-0 group-hover:opacity-100"
                              >
                                <X size={12} />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-b-lg">
                                {shadeCard.extractedColors && shadeCard.extractedColors.length > 0
                                  ? `${shadeCard.extractedColors.length} colors`
                                  : 'Card #' + (index + 1)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Color Extraction Success Message */}
                      {showExtractionSuccess && (
                        <div className="mb-3 flex items-center justify-center gap-2 text-emerald-600 animate-pulse p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs">✓</div>
                          <span className="text-sm font-medium">Colors extracted successfully!</span>
                        </div>
                      )}

                      {/* Upload Shade Card Button */}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => shadeCardInputRef.current?.click()}
                          disabled={isUploadingShadeCard || isExtractingColors}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-dashed border-slate-300 rounded-lg hover:border-indigo-400 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700 disabled:opacity-50"
                        >
                          {isUploadingShadeCard || isExtractingColors ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              <span>{isExtractingColors ? 'Extracting colors...' : 'Uploading...'}</span>
                            </>
                          ) : (
                            <>
                              <UploadCloud size={16} />
                              <span>Upload Shade Card</span>
                            </>
                          )}
                        </button>
                        <input
                          ref={shadeCardInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleShadeCardChange}
                          className="hidden"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Upload shade cards to show color variations. Colors will be automatically extracted.
                      </p>
                    </div>

                    {/* Extracted Color Palette Display */}
                    {extractedColorVariants && extractedColorVariants.length > 0 && (
                      <div className="mt-6 p-4 sm:p-6 bg-slate-50 border border-slate-200 rounded-xl">
                        {/* Color Palette Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                              <Palette size={20} className="text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">All Extracted Colors</h3>
                              <p className="text-sm text-slate-600">
                                {extractedColorVariants.length} color{extractedColorVariants.length !== 1 ? 's' : ''} detected from {fabricShadeCards.length} shade card{fabricShadeCards.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Color Grid - More visible and prominent */}
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3 sm:gap-4">
                          {extractedColorVariants.map((color, idx) => (
                            <div
                              key={idx}
                              className="group relative rounded-lg border border-slate-300 bg-white hover:border-indigo-400 hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer"
                              title={`${color.colorName || 'Color'} - ${color.hex}`}
                            >
                              {/* Color Swatch - Larger */}
                              <div className="relative h-20 sm:h-24 w-full overflow-hidden rounded-t-lg">
                                <div 
                                  className="w-full h-full" 
                                  style={{ backgroundColor: color.hex || '#000' }}
                                />
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-md">
                                    <Palette size={14} className="text-indigo-600" />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Color Info */}
                              <div className="p-2.5 bg-white">
                                <div className="text-xs font-bold text-gray-800 font-mono text-center truncate">
                                  {color.hex}
                                </div>
                                {color.colorName && (
                                  <div className="text-xs text-gray-600 text-center truncate mt-1 font-medium">
                                    {color.colorName}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Summary Footer */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                            <Palette size={14} className="text-indigo-600" />
                            <span className="font-medium">
                              Total: <span className="text-indigo-600 font-bold">{extractedColorVariants.length}</span> unique colors extracted
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>

              {/* Right: Detailed Fabric Analysis */}
              <div className="min-w-0">
                <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText size={18} className="text-indigo-600" />
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900">Detailed Fabric Analysis</h3>
                  </div>

                  {!productDetails?.image ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                      Upload a fabric image to generate and edit fabric properties.
                    </div>
                  ) : (
                    <div className="bg-white border-2 border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
                      {fabricAnalysisLoading ? (
                        <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                          <div className="flex items-center gap-3">
                            <Loader2 size={20} className="text-indigo-600 animate-spin" />
                            <div>
                              <p className="text-sm font-medium text-slate-900">Analyzing fabric details...</p>
                              <p className="text-xs text-indigo-600 mt-1">We are generating the detailed fabric analysis</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setIsFabricAnalysisOpen(prev => !prev)}
                            className="w-full flex items-center justify-between gap-3"
                          >
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm sm:text-base font-semibold text-slate-900">
                                Fabric Properties
                              </h4>
                            </div>
                            {isFabricAnalysisOpen ? (
                              <ChevronUp size={18} className="text-indigo-600" />
                            ) : (
                              <ChevronDown size={18} className="text-indigo-600" />
                            )}
                          </button>

                          {isFabricAnalysisOpen && (
                            <div className="space-y-4 mt-4">
                              {(() => {
                                const a = productDetails?.fabricAnalysis || {};
                                const fa = a.fabric_analysis || {};
                                const vc = a.visual_characteristics || {};
                                const renderArr = (items) => Array.isArray(items) ? items.join(', ') : '';
                                return (
                                  <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">Fabric Type</label>
                                        <TextureSelector
                                          selectedTexture={findTextureByFabricType(fa.fabric_type)}
                                          onTextureChange={(texture) => handleFabricAnalysisFieldChange('fabric_analysis', 'fabric_type', texture ? texture.name : '')}
                                          showTip={false}
                                          onTipToggle={() => {}}
                                          dropdownMode="inline"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">Weave/Knit</label>
                                        <div className="relative">
                                          <select
                                            value={fa.weave_or_knit || ''}
                                            onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'weave_or_knit', e.target.value)}
                                            className="w-full h-11 px-4 pr-10 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm appearance-none"
                                          >
                                            <option value="">Select weave/knit</option>
                                            {[...new Set([...(fa.weave_or_knit && !WEAVE_OR_KNIT_OPTIONS.includes(fa.weave_or_knit) ? [fa.weave_or_knit] : []), ...WEAVE_OR_KNIT_OPTIONS])].map(opt => (
                                              <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                          </select>
                                          <ChevronDown size={18} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">Opacity</label>
                                        <div className="relative">
                                          <select
                                            value={fa.opacity || ''}
                                            onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'opacity', e.target.value)}
                                            className="w-full h-11 px-4 pr-10 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm appearance-none"
                                          >
                                            <option value="">Select opacity</option>
                                            {[...new Set([...(fa.opacity && !OPACITY_OPTIONS.includes(fa.opacity) ? [fa.opacity] : []), ...OPACITY_OPTIONS])].map(opt => (
                                              <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                          </select>
                                          <ChevronDown size={18} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">Stretch</label>
                                        <div className="relative">
                                          <select
                                            value={fa.stretch || ''}
                                            onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'stretch', e.target.value)}
                                            className="w-full h-11 px-4 pr-10 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm appearance-none"
                                          >
                                            <option value="">Select stretch</option>
                                            {[...new Set([...(fa.stretch && !STRETCH_OPTIONS.includes(fa.stretch) ? [fa.stretch] : []), ...STRETCH_OPTIONS])].map(opt => (
                                              <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                          </select>
                                          <ChevronDown size={18} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        </div>
                                      </div>
                                      <div className="sm:col-span-2">
                                        <label className="text-xs text-gray-600 mb-1 block">GSM Weight</label>
                                        <div className="relative">
                                          <select
                                            value={fa.gsm_weight || ''}
                                            onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'gsm_weight', e.target.value)}
                                            className="w-full h-11 px-4 pr-10 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm appearance-none"
                                          >
                                            <option value="">Select GSM weight</option>
                                            {[...new Set([...(fa.gsm_weight && !GSM_WEIGHT_OPTIONS.includes(fa.gsm_weight) ? [fa.gsm_weight] : []), ...GSM_WEIGHT_OPTIONS])].map(opt => (
                                              <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                          </select>
                                          <ChevronDown size={18} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        </div>
                                      </div>
                                    </div>

                                    <div className="bg-blue-50 rounded-lg border border-blue-100 p-3 space-y-3">
                                      <p className="text-xs font-semibold text-blue-800">Visual Composition</p>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">Fabric Visual</label>
                                        <div className="relative">
                                          <select
                                            value={vc.visual_type || ''}
                                            onChange={(e) => handleFabricAnalysisFieldChange('visual_characteristics', 'visual_type', e.target.value)}
                                            className="w-full h-11 px-4 pr-10 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm appearance-none"
                                          >
                                            <option value="">Select fabric visual</option>
                                            {[
                                              ...new Set([
                                                ...(vc.visual_type && !FABRIC_VISUAL_TYPE_OPTIONS.includes(vc.visual_type)
                                                  ? [vc.visual_type]
                                                  : []),
                                                ...FABRIC_VISUAL_TYPE_OPTIONS
                                              ])
                                            ].map((opt) => (
                                              <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                          </select>
                                          <ChevronDown size={18} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        </div>
                                      </div>
                                      <div className="space-y-1 text-xs text-gray-700">
                                        {renderArr(vc.primary_colors) ? (
                                          <div>
                                            <span className="text-gray-500">Primary Colors:</span>{' '}
                                            <span className="font-medium">{renderArr(vc.primary_colors)}</span>
                                          </div>
                                        ) : null}
                                        {renderArr(vc.secondary_colors) ? (
                                          <div>
                                            <span className="text-gray-500">Secondary Colors:</span>{' '}
                                            <span className="font-medium">{renderArr(vc.secondary_colors)}</span>
                                          </div>
                                        ) : null}
                                        {!vc.visual_type && !renderArr(vc.primary_colors) && !renderArr(vc.secondary_colors) && (
                                          <p className="text-gray-500 italic">No other visual characteristics available</p>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}

                          <div className="mt-4">
                            <FabricGarmentInsights
                              fabricAnalysis={productDetails?.fabricAnalysis}
                              selectedCategoryValue={selectedCategory?.value}
                              selectedTypes={selectedTypes}
                              onAddType={(item, audience) => {
                                const typeLabel = item.type_label?.trim();
                                if (!typeLabel) return;
                                const categoryValue = item.category || audience?.category;
                                const categoryMeta = PRODUCT_CATEGORIES_OPTIMIZED.find((c) => c.value === categoryValue);
                                const catalogType = categoryMeta?.types?.find((t) => t.value === item.type_value);
                                const nextType = {
                                  value: catalogType?.value || item.type_value || typeLabel,
                                  label: catalogType?.label || typeLabel,
                                  isCustom: !catalogType,
                                  finalValue: catalogType?.value || item.type_value || typeLabel,
                                  finalLabel: catalogType?.label || typeLabel,
                                  recommendation: { garment: typeLabel, reason: item.reason },
                                  category: categoryValue,
                                  categoryLabel: categoryMeta?.label || audience?.label,
                                };
                                if (selectedTypes.length >= 5) {
                                  showNotification?.('Maximum 5 product types allowed', 'warning');
                                  return;
                                }
                                if (
                                  selectedTypes.some(
                                    (t) =>
                                      t.value === nextType.value ||
                                      t.finalValue === nextType.finalValue ||
                                      t.label === nextType.label ||
                                      t.finalLabel === nextType.finalLabel
                                  )
                                ) {
                                  showNotification?.('This garment is already selected', 'info');
                                  return;
                                }
                                if (categoryMeta && selectedCategory?.value !== categoryValue) {
                                  handleCategorySelect(categoryValue);
                                }
                                handleMultipleTypesChange([...selectedTypes, nextType]);
                                setProductInputMethod('types');
                                showNotification?.(`Added "${nextType.finalLabel}" as product type`, 'success');
                              }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {currentStep === 1 && (
          <div className="flex min-w-0 w-full flex-col h-full">
            {/* Simplified: Removed header and description */}

            {/* Main Content Area */}
            <div className="flex min-w-0 w-full flex-1 flex-col">
              {/* Product Selection Section */}
              <div className="flex min-w-0 w-full flex-1 flex-col">
                {/* Desktop: category on left, AI recs on right */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <div className="min-w-0">
                    {/* Product category: visual selection — horizontal scroll on mobile (cards must not shrink) */}
                    <div className="mb-4 sm:mb-6 w-full min-w-0 max-w-full">
                      <div
                        role="listbox"
                        aria-label="Product category"
                        className="flex w-full max-w-full min-w-0 flex-nowrap gap-2 sm:gap-3 overflow-x-auto overscroll-x-contain pb-2 -mx-3 px-3 sm:-mx-4 sm:px-4 lg:-mx-6 lg:px-6 touch-pan-x [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]"
                      >
                        {PRODUCT_CATEGORIES_OPTIMIZED.map((opt) => {
                          const isSelected = selectedCategory?.value === opt.value;
                          const isDisabled = Boolean(opt.disabled);

                          const kidsBoyCategoryPreview =
                            selectedKidAge === 'toddlers'
                              ? KIDS_BOY_TODDLER_PREVIEW
                              : KIDS_BOY_CATEGORY_PREVIEW;
                          const kidsGirlCategoryPreview =
                            selectedKidAge === 'toddlers'
                              ? KIDS_GIRL_TODDLER_PREVIEW
                              : WOMEN_CLOTHING_CATEGORY_PREVIEW;

                          const visualMetaByValue = {
                            'women-clothing': { previewImg: WOMEN_CLOTHING_CATEGORY_PREVIEW, accent: 'bg-fuchsia-500', ring: 'ring-fuchsia-200' },
                            'men-clothing': { previewImg: MEN_CLOTHING_CATEGORY_PREVIEW, accent: 'bg-blue-600', ring: 'ring-blue-200' },
                            'kids-boy': { previewImg: kidsBoyCategoryPreview, accent: 'bg-sky-500', ring: 'ring-sky-200' },
                            'kids-girl': { previewImg: kidsGirlCategoryPreview, accent: 'bg-pink-500', ring: 'ring-pink-200' },
                          };
                          const meta = visualMetaByValue[opt.value] || { Icon: Users, accent: 'bg-indigo-600', ring: 'ring-indigo-200' };
                          const Icon = meta.Icon;

                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => {
                                if (isDisabled) return;
                                handleCategorySelect(opt.value);
                              }}
                              disabled={isDisabled}
                              aria-pressed={isSelected}
                              className={[
                                'flex-shrink-0 w-[72px] sm:w-[104px]',
                                'rounded-lg sm:rounded-xl border transition-all',
                                'px-1 py-2 sm:px-2 sm:py-3',
                                'flex flex-col items-center justify-center text-center',
                                'bg-white',
                                isDisabled ? 'opacity-50 cursor-not-allowed border-gray-200' : 'hover:shadow-md border-gray-200',
                                isSelected ? 'border-indigo-500 ring-2 ring-indigo-200 shadow-md' : '',
                              ].join(' ')}
                            >
                              <div
                                className={[
                                  'w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl flex items-center justify-center overflow-hidden',
                                  meta.previewImg ? 'bg-white' : meta.accent,
                                  isSelected ? `ring-2 sm:ring-4 ${meta.ring}` : '',
                                ].join(' ')}
                              >
                                {meta.previewImg ? (
                                  <img
                                    src={encodeAssetPath(meta.previewImg)}
                                    alt={opt.label}
                                    className="h-full w-full object-cover object-top"
                                  />
                                ) : (
                                  <Icon className="text-white w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={2} />
                                )}
                              </div>
                              <div className="mt-1 sm:mt-1.5 text-[10px] sm:text-xs font-semibold text-slate-900 leading-tight line-clamp-2 min-h-[2rem] sm:min-h-0 flex items-center justify-center px-0">
                                {opt.label}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Kid Age Dropdown - Mobile Optimized */}
                    {(selectedCategory.value === 'kids-boy' || selectedCategory.value === 'kids-girl') && (
                      <div className="mb-4 sm:mb-6">
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Kid Age Range</label>
                          <select
                            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base bg-white touch-manipulation"
                            value={selectedKidAge}
                            onChange={handleKidAgeChange}
                          >
                            <option value="toddlers">Toddlers (0-4 years)</option>
                            <option value="kids">Kids (4-12 years)</option>
                            <option value="teens">Teens (12-18 years)</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Desktop-only AI recommendations on the right */}
                  <div className="hidden md:block min-w-0">
                    {(() => {
                      // Determine which recommendations to show based on selected category
                      const isMenCategory = selectedCategory?.value === 'men-clothing' || selectedCategory?.value === 'kids-boy';
                      const isWomenCategory = selectedCategory?.value === 'women-clothing' || selectedCategory?.value === 'kids-girl';
                      
                      const menRecommendations = productDetails?.fabricAnalysis?.men_garment_recommendations?.best_suited || [];
                      const womenRecommendations = productDetails?.fabricAnalysis?.women_garment_recommendations?.best_suited || [];
                      
                      const recommendationsToShow = isMenCategory ? menRecommendations : 
                                                     isWomenCategory ? womenRecommendations : 
                                                     womenRecommendations; // Default to women's if category is unclear
                      
                      const hasRecommendations = recommendationsToShow.length > 0;
                      const shouldShow = fabricAnalysisLoading || (productDetails?.image && hasRecommendations);
                      const maxCollapsed = 3;
                      const visibleRecs = showAllAiRecommendations ? recommendationsToShow : recommendationsToShow.slice(0, maxCollapsed);
                      
                      return shouldShow ? (
                      <div className="mb-4 sm:mb-6">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4">
                          <button
                            type="button"
                            className="w-full flex items-center justify-between gap-3"
                            onClick={() => setIsAiRecommendationsOpen((v) => !v)}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Sparkles size={18} className="text-indigo-600" />
                              <h3 className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                                AI Recommended {isMenCategory ? 'Men\'s' : isWomenCategory ? 'Women\'s' : ''} Garments
                              </h3>
                              {!fabricAnalysisLoading && hasRecommendations && (
                                <span className="shrink-0 text-[11px] font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                                  {recommendationsToShow.length}
                                </span>
                              )}
                            </div>
                            {isAiRecommendationsOpen ? (
                              <ChevronUp size={18} className="text-indigo-600 shrink-0" />
                            ) : (
                              <ChevronDown size={18} className="text-indigo-600 shrink-0" />
                            )}
                          </button>
                          {fabricAnalysisLoading ? (
                            <div className="space-y-3">
                              <div className="bg-white rounded-lg border border-slate-200 p-4">
                                <div className="flex items-center gap-3">
                                  <Loader2 size={20} className="text-indigo-600 animate-spin" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-700">Analyzing fabric...</p>
                                    <p className="text-xs text-gray-500 mt-1">AI is analyzing your fabric to recommend the best garments</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : hasRecommendations ? (
                            <div className={isAiRecommendationsOpen ? 'mt-3' : 'mt-2'}>
                              {!isAiRecommendationsOpen ? (
                                <div className="flex flex-wrap gap-2">
                                  {visibleRecs.map((r, idx) => (
                                    <button
                                      key={`${r.garment}-${idx}`}
                                      type="button"
                                      onClick={() => setIsAiRecommendationsOpen(true)}
                                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 transition"
                                      title="Click to view details"
                                    >
                                      <CheckCircle size={14} className="text-indigo-600" />
                                      <span className="truncate max-w-[12rem]">{r.garment}</span>
                                    </button>
                                  ))}
                                  {recommendationsToShow.length > maxCollapsed && !showAllAiRecommendations && (
                                    <button
                                      type="button"
                                      onClick={() => setShowAllAiRecommendations(true)}
                                      className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                                    >
                                      +{recommendationsToShow.length - maxCollapsed} more
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <p className="text-xs text-slate-600 mb-3">
                                    Tap “Add” to include a recommended garment in your kit (max 5).
                                  </p>
                                  <div className="space-y-2">
                                    {visibleRecs.map((recommendation, index) => (
                                      <div
                                        key={index}
                                        className="bg-white rounded-lg border border-slate-200 px-3 py-2"
                                      >
                                        <div className="flex items-center justify-between gap-3">
                                          <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                              <CheckCircle size={16} className="text-indigo-600 flex-shrink-0" />
                                              <h4 className="text-sm font-semibold text-gray-900 truncate">
                                                {recommendation.garment}
                                              </h4>
                                            </div>
                                            {recommendation.reason && (
                                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                                {recommendation.reason}
                                              </p>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const garmentName = recommendation.garment.trim();
                                              const customType = {
                                                value: garmentName,
                                                label: garmentName,
                                                isCustom: true,
                                                finalValue: garmentName,
                                                finalLabel: garmentName,
                                                recommendation: recommendation,
                                                category: selectedCategory?.value,
                                                categoryLabel: selectedCategory?.label
                                              };
                                              
                                              if (selectedTypes.length < 5 && !selectedTypes.some(t => t.label === garmentName || t.finalLabel === garmentName)) {
                                                handleMultipleTypesChange([...selectedTypes, customType]);
                                                setProductInputMethod('types');
                                                if (showNotification) {
                                                  showNotification(`Added "${garmentName}" as product type`, 'success');
                                                }
                                              } else if (selectedTypes.length >= 5) {
                                                if (showNotification) {
                                                  showNotification('Maximum 5 product types allowed', 'warning');
                                                }
                                              } else {
                                                if (showNotification) {
                                                  showNotification('This garment is already selected', 'info');
                                                }
                                              }
                                            }}
                                            disabled={selectedTypes.length >= 5 || selectedTypes.some(t => t.label === recommendation.garment || t.finalLabel === recommendation.garment)}
                                            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                              selectedTypes.length >= 5 || selectedTypes.some(t => t.label === recommendation.garment)
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 shadow-sm'
                                            }`}
                                          >
                                            {selectedTypes.some(t => t.label === recommendation.garment || t.finalLabel === recommendation.garment) ? 'Added' : 'Add'}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {recommendationsToShow.length > maxCollapsed && (
                                    <div className="mt-3 flex items-center justify-between gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setShowAllAiRecommendations((v) => !v)}
                                        className="text-xs font-semibold text-indigo-700 hover:underline"
                                      >
                                        {showAllAiRecommendations ? 'Show less' : `Show all (${recommendationsToShow.length})`}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setIsAiRecommendationsOpen(false)}
                                        className="text-xs font-semibold text-slate-600 hover:underline"
                                      >
                                        Collapse
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="bg-white rounded-lg border border-slate-200 p-4">
                              <p className="text-sm text-gray-600">
                                No {isMenCategory ? 'men\'s' : isWomenCategory ? 'women\'s' : ''} garment recommendations available for this category.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Mobile AI Fabric Analysis Recommendations (keeps previous placement) */}
                <div className="md:hidden">
                {(() => {
                  // Determine which recommendations to show based on selected category
                  const isMenCategory = selectedCategory?.value === 'men-clothing' || selectedCategory?.value === 'kids-boy';
                  const isWomenCategory = selectedCategory?.value === 'women-clothing' || selectedCategory?.value === 'kids-girl';
                  
                  const menRecommendations = productDetails?.fabricAnalysis?.men_garment_recommendations?.best_suited || [];
                  const womenRecommendations = productDetails?.fabricAnalysis?.women_garment_recommendations?.best_suited || [];
                  
                  const recommendationsToShow = isMenCategory ? menRecommendations : 
                                                 isWomenCategory ? womenRecommendations : 
                                                 womenRecommendations; // Default to women's if category is unclear
                  
                  const hasRecommendations = recommendationsToShow.length > 0;
                  const shouldShow = fabricAnalysisLoading || (productDetails?.image && hasRecommendations);
                  const maxCollapsed = 3;
                  const visibleRecs = showAllAiRecommendations ? recommendationsToShow : recommendationsToShow.slice(0, maxCollapsed);
                  
                  return shouldShow ? (
                  <div className="mb-4 sm:mb-6">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between gap-3"
                        onClick={() => setIsAiRecommendationsOpen((v) => !v)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Sparkles size={18} className="text-indigo-600" />
                          <h3 className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                            AI Recommended {isMenCategory ? 'Men\'s' : isWomenCategory ? 'Women\'s' : ''} Garments
                          </h3>
                          {!fabricAnalysisLoading && hasRecommendations && (
                            <span className="shrink-0 text-[11px] font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                              {recommendationsToShow.length}
                            </span>
                          )}
                        </div>
                        {isAiRecommendationsOpen ? (
                          <ChevronUp size={18} className="text-indigo-600 shrink-0" />
                        ) : (
                          <ChevronDown size={18} className="text-indigo-600 shrink-0" />
                        )}
                      </button>
                      {fabricAnalysisLoading ? (
                        <div className="space-y-3">
                          <div className="bg-white rounded-lg border border-slate-200 p-4">
                            <div className="flex items-center gap-3">
                              <Loader2 size={20} className="text-indigo-600 animate-spin" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-700">Analyzing fabric...</p>
                                <p className="text-xs text-gray-500 mt-1">AI is analyzing your fabric to recommend the best garments</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : hasRecommendations ? (
                        <div className={isAiRecommendationsOpen ? 'mt-3' : 'mt-2'}>
                          {!isAiRecommendationsOpen ? (
                            <div className="flex flex-wrap gap-2">
                              {visibleRecs.map((r, idx) => (
                                <button
                                  key={`${r.garment}-${idx}`}
                                  type="button"
                                  onClick={() => setIsAiRecommendationsOpen(true)}
                                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 transition"
                                  title="Click to view details"
                                >
                                  <CheckCircle size={14} className="text-indigo-600" />
                                  <span className="truncate max-w-[12rem]">{r.garment}</span>
                                </button>
                              ))}
                              {recommendationsToShow.length > maxCollapsed && !showAllAiRecommendations && (
                                <button
                                  type="button"
                                  onClick={() => setShowAllAiRecommendations(true)}
                                  className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                                >
                                  +{recommendationsToShow.length - maxCollapsed} more
                                </button>
                              )}
                            </div>
                          ) : (
                            <>
                              <p className="text-xs text-slate-600 mb-3">
                                Tap “Add” to include a recommended garment in your kit (max 5).
                              </p>
                              <div className="space-y-2">
                                {visibleRecs.map((recommendation, index) => (
                                  <div
                                    key={index}
                                    className="bg-white rounded-lg border border-slate-200 px-3 py-2"
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                          <CheckCircle size={16} className="text-indigo-600 flex-shrink-0" />
                                          <h4 className="text-sm font-semibold text-gray-900 truncate">
                                            {recommendation.garment}
                                          </h4>
                                        </div>
                                        {recommendation.reason && (
                                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                            {recommendation.reason}
                                          </p>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const garmentName = recommendation.garment.trim();
                                          const customType = {
                                            value: garmentName,
                                            label: garmentName,
                                            isCustom: true,
                                            finalValue: garmentName,
                                            finalLabel: garmentName,
                                            recommendation: recommendation,
                                            category: selectedCategory?.value,
                                            categoryLabel: selectedCategory?.label
                                          };
                                          
                                          if (selectedTypes.length < 5 && !selectedTypes.some(t => t.label === garmentName || t.finalLabel === garmentName)) {
                                            handleMultipleTypesChange([...selectedTypes, customType]);
                                            setProductInputMethod('types');
                                            if (showNotification) {
                                              showNotification(`Added "${garmentName}" as product type`, 'success');
                                            }
                                          } else if (selectedTypes.length >= 5) {
                                            if (showNotification) {
                                              showNotification('Maximum 5 product types allowed', 'warning');
                                            }
                                          } else {
                                            if (showNotification) {
                                              showNotification('This garment is already selected', 'info');
                                            }
                                          }
                                        }}
                                        disabled={selectedTypes.length >= 5 || selectedTypes.some(t => t.label === recommendation.garment || t.finalLabel === recommendation.garment)}
                                        className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                          selectedTypes.length >= 5 || selectedTypes.some(t => t.label === recommendation.garment)
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 shadow-sm'
                                        }`}
                                      >
                                        {selectedTypes.some(t => t.label === recommendation.garment || t.finalLabel === recommendation.garment) ? 'Added' : 'Add'}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {recommendationsToShow.length > maxCollapsed && (
                                <div className="mt-3 flex items-center justify-between gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setShowAllAiRecommendations((v) => !v)}
                                    className="text-xs font-semibold text-indigo-700 hover:underline"
                                  >
                                    {showAllAiRecommendations ? 'Show less' : `Show all (${recommendationsToShow.length})`}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setIsAiRecommendationsOpen(false)}
                                    className="text-xs font-semibold text-slate-600 hover:underline"
                                  >
                                    Collapse
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="bg-white rounded-lg border border-slate-200 p-4">
                          <p className="text-sm text-gray-600">
                            No {isMenCategory ? 'men\'s' : isWomenCategory ? 'women\'s' : ''} garment recommendations available for this category.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  ) : null;
                })()}
                </div>

                {/* Input Method Selection - Mobile Optimized */}
                <div className="mb-4 sm:mb-6">
                      <div className="space-y-3 sm:space-y-4">
                        <div className="w-full">
                          <div className="w-full rounded-xl border-2 border-gray-100 bg-white shadow-sm p-3">
                            <MultiProductTypeSelector
                              inline={useInlineProductSelector}
                              selectedCategory={selectedCategory}
                              selectedTypes={selectedTypes}
                              kidAgeRange={selectedKidAge}
                              onTypesChange={handleProductTypesChange}
                              onActiveCategoryChange={(categoryValue) => handleCategorySelect(categoryValue)}
                              showTip={false}
                              onTipToggle={() => {}}
                              maxSelections={5}
                              selectedDesignPreview={selectedInspirationDesign}
                              onClearSelectedDesign={() => {
                                setSelectedFabricTemplate(null);
                                setSelectedInspirationDesign(null);
                                setProductInputMethod('types');
                                setProductDetails((prev) => {
                                  const fabricProduct = prev.selectedProduct || null;
                                  return {
                                    ...prev,
                                    inspirationalDesign: null,
                                    inspirationalDesignId: null,
                                    inspirationalProductType: '',
                                    name: fabricProduct?.name || '',
                                    type: '',
                                    description: fabricProduct?.description || '',
                                  };
                                });
                              }}
                            />
                            <div className="mt-2 text-[11px] text-gray-500">
                              Select up to 5 types. Change category above to add types from different categories (e.g., Women Dress + Men Kurta).
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-center">
                          <span className="text-xs font-semibold text-gray-400 select-none bg-white/80 px-3 py-1 rounded-full shadow-sm">or</span>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <button
                            type="button"
                            onClick={() => {
                              setIsInspirationModalOpen(true);
                              setInspirationSearchTerm('');
                              loadMyInspirations();
                            }}
                            className="w-full flex items-center justify-center gap-3 px-4 py-4 rounded-xl border-2 border-dashed border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-white active:scale-95 shadow-sm text-base font-medium transition-all touch-manipulation"
                          >
                            <Palette size={20} />
                            <span className="text-center">
                              {selectedInspirationDesign ? 'Change inspiration design' : 'Pick design from inspiration'}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsDesignTemplateModalOpen(true);
                              setPreviewFabricTemplate(null);
                            }}
                            className="w-full flex items-center justify-center gap-3 px-4 py-4 rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/60 text-indigo-800 hover:border-indigo-400 hover:bg-indigo-50 active:scale-95 shadow-sm text-base font-medium transition-all touch-manipulation"
                          >
                            <LayoutTemplate size={20} />
                            <span className="text-center">
                              {selectedFabricTemplate ? 'Change fabric template' : 'Choose from template'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Selected Inspiration Design Thumbnail */}
                    {selectedInspirationDesign && !selectedFabricTemplate && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-white rounded-lg border border-blue-200 overflow-hidden">
                            {selectedInspirationDesign.imageUrl ? (
                              <>
                                <img
                                  src={selectedInspirationDesign.imageUrl}
                                  alt={selectedInspirationDesign.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                  onLoad={(e) => {
                                    e.target.nextSibling.style.display = 'none';
                                  }}
                                />
                                <div className="hidden w-full h-full items-center justify-center text-blue-400 bg-blue-50">
                                  <Palette size={16} />
                                </div>
                              </>
                            ) : (
                              <div className="flex w-full h-full items-center justify-center text-blue-400 bg-blue-50">
                                <Palette size={16} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-900">{selectedInspirationDesign.name}</p>
                            <p className="text-xs text-blue-600">{selectedInspirationDesign.category}</p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedInspirationDesign(null);
                              setProductInputMethod('types'); // Reset to types method
                              setProductDetails(prev => ({
                                ...prev,
                                inspirationalDesign: null,
                                inspirationalDesignId: null,
                                inspirationalProductType: '',
                                name: '', // Clear the product name
                                type: '', // Clear the product type
                                description: '' // Clear the description
                              }));
                            }}
                            className="p-1 hover:bg-blue-100 rounded text-blue-600 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    )}

                  



              </div>
            </div>
          </div>
        )}
        {currentStep === 2 && (
          <div className="min-w-0 w-full">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 flex items-center">
              <Sparkles size={18} className="sm:w-5 sm:h-5 mr-2 text-indigo-600"/>
              Content Settings
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
              {/* Left: Optional settings */}
              <div className="min-w-0">
                {selectedFabricTemplate && (
                  <div className="mb-4">
                    <FabricTemplateSetupFields
                      template={selectedFabricTemplate}
                      fabricName={productDetails?.name || ''}
                      onFabricNameChange={(val) => setProductDetails((prev) => ({ ...prev, name: val }))}
                      companyName={branding?.brandName || ''}
                      onCompanyNameChange={(val) => setBranding && setBranding({ ...(branding || {}), brandName: val })}
                      logoUrl={branding?.logo || ''}
                      logoUploading={brandLogoUploading}
                      onLogoFileSelected={(file) => handleBrandLogoChange && handleBrandLogoChange(file)}
                      onRemoveLogo={() => setBranding && setBranding({ ...(branding || {}), logo: null })}
                      shadeCards={fabricShadeCards}
                      shadeCardUploading={isUploadingShadeCard || isExtractingColors}
                      onShadeCardFileSelected={(file) => handleShadeCardUpload(file)}
                      onRemoveShadeCard={(index) => handleRemoveShadeCard(index)}
                      garmentGroups={fabricTemplateGarmentGroups}
                      selectedGarments={selectedFabricTemplateGarments}
                      onToggleGarment={toggleTemplateGarment}
                      maxGarments={MAX_TEMPLATE_GARMENTS}
                      phone={branding?.phone || ''}
                      onPhoneChange={(val) => setBranding && setBranding({ ...(branding || {}), phone: val })}
                      address={branding?.address || ''}
                      onAddressChange={(val) => setBranding && setBranding({ ...(branding || {}), address: val })}
                      website={branding?.website || ''}
                      onWebsiteChange={(val) => setBranding && setBranding({ ...(branding || {}), website: val })}
                      showErrors={templateSetupAttempted}
                      fabricProperties={
                        <FabricTemplatePropertiesEditor
                          embedded
                          draft={templatePropertyDraft}
                          onChange={(next) => {
                            templatePropertiesDirtyRef.current = true;
                            setTemplatePropertyDraft(next);
                          }}
                        />
                      }
                    />
                  </div>
                )}
                <div className="w-full flex items-center p-3 border rounded-lg bg-slate-50 border-slate-300">
                  <div className="flex items-center">
                    <Settings size={16} className="mr-2 text-indigo-600" />
                    <span className="text-sm font-semibold text-slate-700">Optional Settings</span>
                  </div>
                </div>

                <div className="mt-3 p-3 sm:p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                  {!selectedFabricTemplate && (
                  <>
                  {/* Brand Name Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Briefcase size={16} className="mr-2 text-indigo-600" />
                      Brand Name
                    </label>
                    <input
                      type="text"
                      name="brandName"
                      value={branding?.brandName || ''}
                      onChange={(e) => {
                        if (handleGenerateKitBrandingChange) {
                          handleGenerateKitBrandingChange(e);
                        } else if (setBranding) {
                          setBranding({ ...branding, brandName: e.target.value });
                        }
                      }}
                      placeholder="Enter brand name to display in background"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Optional: Enter your brand name to be displayed in the background of generated images
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <ImageIcon size={16} className="mr-2 text-indigo-600" />
                      Brand Logo
                    </label>
                    <div
                      className="border-2 border-dashed border-slate-300 rounded-lg p-3 bg-slate-50 hover:border-indigo-400 transition"
                    >
                      {brandLogoUploading ? (
                        <div className="flex items-center gap-2 text-sm text-indigo-600">
                          <Loader2 size={16} className="animate-spin" />
                          Uploading logo...
                        </div>
                      ) : branding?.logo ? (
                        <div className="flex items-center gap-3">
                          <img
                            src={branding.logo}
                            alt="Brand logo"
                            className="w-14 h-14 object-contain rounded-lg border border-slate-200 bg-white p-1"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700">Logo uploaded</p>
                            <p className="text-xs text-slate-500">Shown subtly in generated image backgrounds</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => brandLogoInputRef.current?.click()}
                              className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-white"
                            >
                              Replace
                            </button>
                            <button
                              type="button"
                              onClick={() => setBranding?.({ ...branding, logo: null })}
                              className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="w-14 h-14 rounded-lg border border-slate-200 bg-white flex items-center justify-center shrink-0">
                            <ImageIcon size={22} className="text-slate-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-slate-700">Upload your brand logo</p>
                            <p className="text-xs text-slate-500 mt-0.5">PNG or JPG recommended</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => brandLogoInputRef.current?.click()}
                            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                          >
                            <UploadCloud size={16} />
                            Upload logo
                          </button>
                        </div>
                      )}
                      <input
                        ref={brandLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file && handleBrandLogoChange) {
                            handleBrandLogoChange(file);
                          }
                          e.target.value = '';
                        }}
                      />
                    </div>
                    {openBrandModal && (
                      <button
                        type="button"
                        onClick={openBrandModal}
                        className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                      >
                        Or choose from brand library
                      </button>
                    )}
                  </div>
                  </>
                  )}

                {!selectedFabricTemplate && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Environment Settings
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {ENVIRONMENT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setEnvironmentPreset(option.value);
                          setProductDetails((prev) => ({ ...prev, scenePreset: option.value }));
                        }}
                        className={`text-left px-3 py-2 rounded-lg border transition-all ${
                          environmentPreset === option.value
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200 bg-white hover:border-indigo-300'
                        }`}
                      >
                        <div className="text-sm font-semibold text-slate-800">{option.label}</div>
                        <div className="text-xs text-slate-500 mt-1">{option.description}</div>
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={customEnvironmentPrompt}
                    onChange={(e) => {
                      setCustomEnvironmentPrompt(e.target.value);
                      setProductDetails((prev) => ({ ...prev, scene: e.target.value }));
                    }}
                    placeholder="Custom environment (e.g., premium boutique interior with warm lighting, marble floor, soft plants)"
                    className="w-full mt-3 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none"
                    rows={3}
                    maxLength={280}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Pick a preset and/or describe your own scene. Custom notes are combined with the selected environment.
                  </p>
                </div>
                )}

                {!selectedFabricTemplate && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <UserCircle size={16} className="mr-2 text-indigo-600" />
                    Virtual Try-On (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Capture user photo from camera for model face personalization.
                  </p>
                  <div
                    className="border-2 border-dashed border-slate-300 rounded-lg p-3 bg-slate-50 hover:border-indigo-400 transition cursor-pointer"
                    onClick={() => {
                      if (modelImageUploading) return;
                      openCamera('model', 'user');
                    }}
                  >
                    {modelImageUploading ? (
                      <div className="flex items-center gap-2 text-sm text-indigo-600">
                        <Loader2 size={16} className="animate-spin" />
                        Processing captured image...
                      </div>
                    ) : productDetails.modelImage ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={productDetails.modelImage}
                          alt="Model"
                          className="w-14 h-14 object-cover rounded-lg border border-slate-200"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">Virtual try-on image captured</p>
                          <p className="text-xs text-slate-500">Click to retake from camera</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProductDetails(prev => ({ ...prev, modelImage: null }));
                          }}
                          className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Camera size={16} />
                        Open camera for virtual try-on
                      </div>
                    )}
                  </div>
                </div>
                )}

                {!selectedFabricTemplate && (
                <div className={`mb-4 p-4 border rounded-lg ${closeupShotLocked ? 'border-amber-300 bg-amber-50' : 'border-indigo-200 bg-indigo-50/40'}`}>
                  <label className={`flex items-start gap-3 text-sm font-medium text-gray-700 ${closeupShotLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:cursor-not-allowed"
                      checked={closeupShotEnabled}
                      disabled={closeupShotLocked}
                      onChange={(e) => {
                        if (closeupShotLocked) return;
                        setEnableCloseupshot(e.target.checked);
                      }}
                    />
                    <div>
                      <span className={`font-semibold inline-flex items-center gap-1.5 ${closeupShotLocked ? 'text-slate-700' : 'text-indigo-800'}`}>
                        <ZoomIn size={15} className={closeupShotLocked ? 'text-amber-600' : 'text-indigo-600'} />
                        Generate close-up shot
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        Move the camera in very close so weave, print, and fabric texture are clearly visible — not a full-body shot.
                      </p>
                    </div>
                  </label>
                  {closeupShotLocked ? (
                    <button
                      type="button"
                      onClick={() => setActiveView?.('pricing')}
                      className="mt-3 ml-7 inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold text-amber-950 shadow-sm ring-1 ring-amber-500/40 transition hover:bg-amber-300"
                    >
                      Available on Basic and Advance plans
                      <ArrowRight size={13} />
                    </button>
                  ) : null}
                </div>
                )}

                {/* Multi Color Generation */}
                {hasMultipleColorVariants && (
                  <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50/40">
                    <label className="flex items-start gap-3 text-sm font-medium text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={generateMultiColorImage}
                        onChange={(e) => setGenerateMultiColorImage(e.target.checked)}
                      />
                      <div>
                        <span className="font-semibold text-blue-700">Generate multi-color showcase</span>
                        <p className="text-xs text-gray-600 mt-1">
                          Create one hero image featuring {totalColorShades} shade{totalColorShades === 1 ? '' : 's'} of this fabric on different models, perfect for lookbook spreads.
                        </p>
                      </div>
                    </label>
                  </div>
                )}
                
                {/* Additional Advanced Settings can be added here */}
                <div className="text-xs text-gray-500 text-center">
                  More advanced settings coming soon...
                </div>
              </div>
              </div>

              {/* Right: Summary (generate button moved to bottom bar) */}
              {!liteMode && (
              <div className="min-w-0 md:sticky md:top-4">
                <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
                  <div className="text-sm font-semibold text-slate-900 mb-2">Summary</div>
                  <p className="text-xs text-slate-500">
                    Your generate action is available in the bottom bar.
                  </p>

                  {!selectedInspirationDesign && !selectedFabricTemplate && selectedTypes.length === 0 && (
                    <p className="text-xs text-gray-500 text-center mt-3">
                      Select a product type, inspiration design, or fabric template to continue
                    </p>
                  )}

                  {productInputMethod === 'types' && selectedTypes.length > 0 && (
                    <p className="text-xs text-indigo-600 text-center mt-3">
                      Will generate {selectedTypes.length} separate kits for selected product types ({selectedTypes.length * (fourKEnabled ? 2 : 1)} image credit{selectedTypes.length * (fourKEnabled ? 2 : 1) === 1 ? '' : 's'} required)
                      {selectedTexture && (
                        <span className="block mt-1 text-indigo-600">
                          with {selectedTexture.name} texture
                        </span>
                      )}
                    </p>
                  )}

                  {selectedInspirationDesign && productDetails.inspirationalDesign && productDetails.name?.trim() && (
                    <p className="text-xs text-indigo-600 text-center mt-3">
                      Will generate 1 kit based on your inspirational design and "{productDetails.name?.trim()}" description ({fourKEnabled ? 2 : 1} image credit{fourKEnabled ? 's' : ''} required)
                      {selectedTexture && (
                        <span className="block mt-1 text-indigo-600">
                          with {selectedTexture.name} texture
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              )}
            </div>

            {/* Generate and Save to Kit Button (kept for mobile spacing) */}
            <div className="mt-6 md:hidden">
              <button
                onClick={handleMultipleKitGeneration}
                disabled={
                  isMultipleKitGenerating || 
                  !productDetails.category || 
                  (fabricType === 'unstitched' && !selectedInspirationDesign && !selectedFabricTemplate && selectedTypes.length === 0) ||
                  !productDetails.image ||
                  !productDetails.image.startsWith('http')
                }
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white font-semibold py-4 px-6 rounded-lg shadow-md flex items-center justify-center transition-all duration-300 text-base sm:text-lg disabled:cursor-not-allowed touch-manipulation active:scale-95"
              >
                {isMultipleKitGenerating ? (
                  <>
                    <Loader2 size={18} className="sm:w-5 sm:h-5 animate-spin mr-2" />
                    Generating Multiple Kits...
                  </>
                ) : (
                  <>
                    <Layers size={18} className="sm:w-5 sm:h-5 mr-2" />
                    Generate Design
                  </>
                )}
              </button>
              
              {!selectedInspirationDesign && !selectedFabricTemplate && selectedTypes.length === 0 && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Select a product type, inspiration design, or fabric template to continue
                </p>
              )}
              
              
              
              {productInputMethod === 'types' && selectedTypes.length > 0 && (
                <p className="text-xs text-indigo-600 text-center mt-2">
                  Will generate {selectedTypes.length} separate kits for selected product types ({selectedTypes.length * (fourKEnabled ? 2 : 1)} image credit{selectedTypes.length * (fourKEnabled ? 2 : 1) === 1 ? '' : 's'} required)
                  {selectedTexture && (
                    <span className="block mt-1 text-indigo-600">
                      with {selectedTexture.name} texture
                    </span>
                  )}
                </p>
              )}
              
              {selectedInspirationDesign && productDetails.inspirationalDesign && productDetails.name?.trim() && (
                <p className="text-xs text-indigo-600 text-center mt-2">
                  Will generate 1 kit based on your inspirational design and "{productDetails.name?.trim()}" description ({fourKEnabled ? 2 : 1} image credit{fourKEnabled ? 's' : ''} required)
                  {selectedTexture && (
                    <span className="block mt-1 text-indigo-600">
                      with {selectedTexture.name} texture
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        )}
        </div>

        {liteMode && <div className="h-24" />}

        {/* Navigation Buttons */}
        <div
          className={[
            liteMode ? 'fixed bottom-0 left-0 right-0 z-50' : 'sticky bottom-0',
            '-mx-3 sm:-mx-5 lg:-mx-6 px-3 sm:px-5 lg:px-6 pt-3 sm:pt-4 pb-safe-overlay sm:pb-4 bg-white/85 backdrop-blur border-t border-slate-200 flex justify-between',
          ].join(' ')}
        >
          <button
            onClick={goBack}
            disabled={currentStep === 0}
            className="px-4 sm:px-8 py-3 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg shadow-sm transition-all duration-300 font-medium flex items-center disabled:opacity-50 text-sm sm:text-base"
          >
            <ArrowLeft size={18} className="sm:w-5 sm:h-5 mr-2 sm:mr-3" /> Previous
          </button>
          {currentStep === steps.length - 1 ? (
            <button
              onClick={handleMultipleKitGeneration}
              disabled={
                isMultipleKitGenerating || 
                !productDetails.category || 
                (fabricType === 'unstitched' && !selectedInspirationDesign && !selectedFabricTemplate && selectedTypes.length === 0) ||
                !productDetails.image ||
                !productDetails.image.startsWith('http')
              }
              className="px-4 sm:px-8 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white rounded-lg shadow-sm transition-all duration-300 font-semibold flex items-center text-sm sm:text-base disabled:cursor-not-allowed"
            >
              {isMultipleKitGenerating ? (
                <>
                  <Loader2 size={18} className="sm:w-5 sm:h-5 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Layers size={18} className="sm:w-5 sm:h-5 mr-2" />
                  Generate Design
                </>
              )}
            </button>
          ) : (
            <button
              onClick={goNext}
              disabled={
                (currentStep === 0 && (
                  productImageUploading ||
                  fabricAnalysisLoading ||
                  !hasFabricImageUploaded ||
                  !hasFabricAnalysisCompleted
                )) ||
                (currentStep === 1 && !selectedCategory) ||
                (currentStep === 1 && !selectedInspirationDesign && !selectedFabricTemplate && selectedTypes.length === 0)
              }
              className="px-4 sm:px-8 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white rounded-lg shadow-sm transition-all duration-300 font-medium flex items-center text-sm sm:text-base disabled:cursor-not-allowed"
            >
              Next Step <ArrowRight size={18} className="sm:w-5 sm:h-5 ml-2 sm:ml-3" />
            </button>
          )}
        </div>
      </div>
      <GenerateKitModernLoader isVisible={isLoading} progress={loaderProgress} currentStep={loaderStep} />
      {isProductModalOpen && (
        <ProductSelectionModal
          isOpen={isProductModalOpen}
          onClose={() => setIsProductModalOpen(false)}
          onSelect={(product) => {
            applySelectedProduct(product);
            setIsProductModalOpen(false);
          }}
        />
      )}
      {/* Fabric capture: guided guardrails (Generate Design step 1) */}
      {isCameraOpen && cameraCaptureTarget === 'fabric' && stream && (
        <FabricCaptureGuardrailsModal
          stream={stream}
          onClose={closeCamera}
          showNotification={showNotification}
          onValidatedCapture={async (file) => {
            closeCamera();
            setTimeout(() => {
              resetShadeCardSelection();
              setDefaultFabricDraft(null);
              handleProductImageChange(file);
            }, 200);
          }}
        />
      )}

      {/* Model / VTO camera: simple capture */}
      {isCameraOpen && cameraCaptureTarget === 'model' && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm">
          <div className="relative w-full h-full max-w-4xl max-h-[90vh] flex flex-col">
            <button
              onClick={closeCamera}
              className="absolute top-4 right-4 z-10 bg-white/20 backdrop-blur-sm text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Close camera"
            >
              <X size={24} />
            </button>

            <div className="flex-1 relative bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-contain"
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-6 pt-6 pb-safe-nav">
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={switchCamera}
                  className="bg-white/20 backdrop-blur-sm text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-white/30 transition-colors"
                  aria-label="Switch camera"
                >
                  <Camera size={20} />
                </button>

                <button
                  type="button"
                  onClick={capturePhoto}
                  className="bg-white rounded-full w-16 h-16 flex items-center justify-center hover:bg-gray-200 transition-colors shadow-lg border-4 border-gray-300"
                  aria-label="Capture photo"
                >
                  <div className="w-12 h-12 bg-white rounded-full border-2 border-gray-400"></div>
                </button>

                <button
                  type="button"
                  onClick={closeCamera}
                  className="bg-white/20 backdrop-blur-sm text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-white/30 transition-colors"
                  aria-label="Cancel"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <MultipleKitProgress 
        isVisible={isMultipleKitGenerating}
        progress={multipleKitProgress.current}
        currentStep={multipleKitProgress.current}
        totalSteps={multipleKitProgress.total}
        currentProductType={multipleKitProgress.productType}
        status={multipleKitProgress.status}
        error={multipleKitProgress.error}
      />

      {/* Inspiration Design Selection Modal */}
      {isInspirationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="flex h-[100dvh] w-full max-h-[100dvh] flex-col overflow-hidden bg-white shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-xl">
            <div className="flex shrink-0 items-center justify-between border-b px-4 pb-4 pt-safe-overlay sm:p-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Inspiration Design</h3>
              <button
                onClick={() => setIsInspirationModalOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full transition hover:bg-gray-100 active:scale-95"
                aria-label="Close"
              >
                <X size={22} className="text-gray-500" />
              </button>
            </div>

            <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-800">
                {isAdmin ? 'All user designs' : 'My inspirations'}
                {myInspirations.length > 0 ? (
                  <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-200 text-slate-700">
                    {myInspirations.length}
                  </span>
                ) : null}
              </h4>
            </div>

            <div className="p-4 border-b bg-gray-50">
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder={isAdmin ? "Search all user designs..." : "Search your inspirations..."}
                    value={inspirationSearchTerm}
                    onChange={(e) => setInspirationSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <button
                  onClick={() => setShowAddInspirationModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md whitespace-nowrap"
                >
                  <Plus size={18} />
                  <span className="hidden sm:inline">Add New</span>
                  <span className="sm:hidden">Add</span>
                </button>
              </div>
            </div>
            
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {(() => {
                  if (inspirationsLoading) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <Loader2 size={48} className="mb-4 text-indigo-600 animate-spin" />
                        <p className="text-lg font-medium">{isAdmin ? 'Loading all user designs...' : 'Loading your inspirations...'}</p>
                      </div>
                    );
                  }

                  const filteredMyInspirations = myInspirations.filter(design => {
                    const matchesSearch = !inspirationSearchTerm || 
                      design.productName.toLowerCase().includes(inspirationSearchTerm.toLowerCase()) ||
                      (isAdmin && design.user && (
                        design.user.name?.toLowerCase().includes(inspirationSearchTerm.toLowerCase()) ||
                        design.user.email?.toLowerCase().includes(inspirationSearchTerm.toLowerCase())
                      ));
                    return matchesSearch;
                  });

                  if (filteredMyInspirations.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <Eye size={48} className="mb-4 text-gray-300" />
                        <p className="text-lg font-medium">
                          {myInspirations.length === 0 
                            ? (isAdmin ? 'No user designs yet' : 'No inspirations yet')
                            : 'No matching inspirations'}
                        </p>
                        <p className="text-sm mt-2">
                          {myInspirations.length === 0 
                            ? (isAdmin ? 'Users can upload designs in the Inspirations page' : 'Go to Inspirations page to add your designs')
                            : 'Try adjusting your search'}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {filteredMyInspirations.map((inspiration) => (
                        <div
                          key={inspiration._id}
                          onClick={() => {
                            applyInspirationDesignSelection({
                              id: inspiration._id,
                              name: inspiration.productName,
                              category: 'My Inspiration',
                              imageUrl: inspiration.imageUrl,
                            });
                            setIsInspirationModalOpen(false);
                          }}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:shadow-lg ${
                            selectedInspirationDesign?.id === inspiration._id 
                              ? 'border-indigo-500 shadow-lg' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="aspect-square bg-gray-100 flex items-center justify-center">
                            <img 
                              src={inspiration.imageUrl} 
                              alt={inspiration.productName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                              onLoad={(e) => {
                                e.target.nextSibling.style.display = 'none';
                              }}
                            />
                            <div className="hidden w-full h-full items-center justify-center text-gray-400 bg-gray-100">
                              <div className="text-center">
                                <Eye size={24} className="mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Loading...</p>
                              </div>
                            </div>
                          </div>
                          <div className="p-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{inspiration.productName}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(inspiration.createdAt).toLocaleDateString()}
                            </p>
                            {isAdmin && inspiration.user && (
                              <p className="text-xs text-gray-400 mt-1 truncate">
                                {inspiration.user.name || inspiration.user.email || 'Unknown User'}
                              </p>
                            )}
                          </div>
                          {selectedInspirationDesign?.id === inspiration._id && (
                            <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full p-1">
                              <Check size={16} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      )}

      {/* Fabric Template Selection Modal */}
      {isDesignTemplateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="ftl-root flex h-[100dvh] w-full max-h-[100dvh] flex-col overflow-hidden bg-[#faf7f1] shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-5xl sm:rounded-xl">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#e0d8cb] bg-white/80 px-4 pb-4 pt-safe-overlay sm:p-4">
              <div className="min-w-0">
                {!previewFabricTemplate && (
                  <>
                    <h3 className="ftl-serif text-lg font-semibold text-[#1a2744]">
                      Choose from fabric template
                    </h3>
                    <p className="text-xs text-[#5c564e] mt-0.5">
                      Browse promotional fabric flyer templates
                    </p>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!previewFabricTemplate && (
                  <button
                    type="button"
                    onClick={() => setShowAddFabricTemplateModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (previewFabricTemplate) {
                      setPreviewFabricTemplate(null);
                      return;
                    }
                    setIsDesignTemplateModalOpen(false);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full transition hover:bg-black/5 active:scale-95"
                  aria-label={previewFabricTemplate ? 'Back to templates' : 'Close'}
                >
                  {previewFabricTemplate ? <ArrowLeft size={22} className="text-gray-500" /> : <X size={22} className="text-gray-500" />}
                </button>
              </div>
            </div>

            {previewFabricTemplate ? (
              <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-5 bg-[#121018]">
                {previewFabricTemplate.templateKind === 'html' && previewFabricTemplate.htmlContent ? (
                  <FabricTemplateHtmlFrame
                    html={previewFabricTemplate.htmlContent}
                    title={previewFabricTemplate.name}
                    className="min-h-[50vh] h-[50vh] max-h-[60vh] rounded-lg"
                  />
                ) : previewFabricTemplate.thumbnail || previewFabricTemplate.thumbnailUrl ? (
                  <img
                    src={previewFabricTemplate.thumbnail || previewFabricTemplate.thumbnailUrl}
                    alt={previewFabricTemplate.name}
                    className="w-full rounded-lg object-contain max-h-[60vh] mx-auto"
                  />
                ) : previewFabricTemplate.Component ? (
                  (() => {
                    const PreviewFlyer = previewFabricTemplate.Component;
                    return <PreviewFlyer />;
                  })()
                ) : (
                  <img
                    src={previewFabricTemplate.designImage || previewFabricTemplate.thumbnail}
                    alt={previewFabricTemplate.name}
                    className="w-full rounded-lg object-contain max-h-[60vh] mx-auto"
                  />
                )}
                <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
                  {previewFabricTemplate.canManage && (
                    <button
                      type="button"
                      onClick={() => handleDeleteFabricTemplate(previewFabricTemplate)}
                      disabled={deletingFabricTemplateId === (previewFabricTemplate._id || previewFabricTemplate.id)}
                      className="px-4 py-2.5 rounded-lg border border-red-400/40 text-red-300 hover:bg-red-500/20 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {deletingFabricTemplateId === (previewFabricTemplate._id || previewFabricTemplate.id) ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setPreviewFabricTemplate(null)}
                    className="px-4 py-2.5 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 text-sm font-medium"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      applyFabricTemplateSelection(previewFabricTemplate);
                      setPreviewFabricTemplate(null);
                      setIsDesignTemplateModalOpen(false);
                      showNotification?.(`Selected “${previewFabricTemplate.name}” template`, 'success');
                    }}
                    className="px-4 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-semibold"
                  >
                    Use this template
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                  {filteredDesignTemplates.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-[#5c564e]">
                      <LayoutTemplate size={48} className="mb-4 text-[#cfc6b8]" />
                      <p className="ftl-serif text-lg font-medium text-[#1a2744]">No templates yet</p>
                      <p className="text-sm mt-2">Templates you add will appear here</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {filteredDesignTemplates.map((template) => {
                        const isSelected = selectedFabricTemplate?.id === template.id;
                        const Thumb = template.Component;
                        return (
                          <div
                            key={template.id}
                            className={`relative rounded-xl overflow-hidden border-2 transition-all bg-white ${
                              isSelected
                                ? 'border-indigo-500 shadow-lg'
                                : 'border-[#e0d8cb] hover:border-[#1a2744]/30'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => setPreviewFabricTemplate(template)}
                              className="w-full text-left hover:shadow-md transition-all"
                            >
                              <div className="relative aspect-[3/4] overflow-hidden bg-[#ebe4d8] flex items-center justify-center">
                                {template.thumbnail || template.designImage ? (
                                  <img
                                    src={template.thumbnail || template.designImage}
                                    alt={template.name}
                                    className="absolute inset-0 w-full h-full object-contain"
                                    loading="lazy"
                                  />
                                ) : template.templateKind === 'html' && template.htmlContent ? (
                                  <div className="absolute inset-0">
                                    <FabricTemplateHtmlFrame
                                      html={template.htmlContent}
                                      title={template.name}
                                      fill
                                      className="pointer-events-none"
                                    />
                                  </div>
                                ) : Thumb ? (
                                  <div className="ftl-flyer--thumb absolute inset-0 overflow-hidden">
                                    <Thumb />
                                  </div>
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center text-[#5c564e]">
                                    <LayoutTemplate size={28} />
                                  </div>
                                )}
                                <span
                                  className="absolute top-2 left-2 text-[0.5rem] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full text-white"
                                  style={{ background: template.accent }}
                                >
                                  {template.category}
                                </span>
                                {isSelected && (
                                  <div className="absolute bottom-2 right-2 bg-indigo-600 text-white rounded-full p-1">
                                    <Check size={14} />
                                  </div>
                                )}
                              </div>
                            </button>
                            {template.canManage && (
                              <button
                                type="button"
                                onClick={() => handleDeleteFabricTemplate(template)}
                                disabled={deletingFabricTemplateId === (template._id || template.id)}
                                className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/90 text-red-600 shadow hover:bg-red-50 disabled:opacity-50"
                                aria-label={`Delete ${template.name}`}
                              >
                                {deletingFabricTemplateId === (template._id || template.id) ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <Trash2 size={14} />
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <AddFabricTemplateModal
        open={showAddFabricTemplateModal}
        onClose={() => setShowAddFabricTemplateModal(false)}
        showNotification={showNotification}
        onCreated={async (created) => {
          await refreshFabricTemplates();
          const hydrated = hydrateFabricTemplate(created);
          if (hydrated) setPreviewFabricTemplate(hydrated);
        }}
      />

      {/* Add New Inspiration Modal */}
      {showAddInspirationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Add New Inspiration</h2>
              <button
                onClick={() => {
                  setShowAddInspirationModal(false);
                  setNewInspirationData({ imageUrl: '', productName: '', imageFile: null });
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={addingInspiration}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Product Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newInspirationData.productName}
                  onChange={(e) => setNewInspirationData({ ...newInspirationData, productName: e.target.value })}
                  placeholder="e.g., Vintage T-Shirt"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={addingInspiration}
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Image <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleInspirationFileSelect}
                    className="hidden"
                    id="new-inspiration-image-upload"
                    disabled={addingInspiration}
                  />
                  <label 
                    htmlFor="new-inspiration-image-upload" 
                    className="cursor-pointer"
                  >
                    {newInspirationData.imageFile ? (
                      <div className="space-y-2">
                        <BlobPreview
                          file={newInspirationData.imageFile}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg mb-2"
                        />
                        <p className="text-sm text-gray-600">{newInspirationData.imageFile.name}</p>
                        <p className="text-xs text-indigo-600">Click to change</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                          <Plus size={24} className="text-indigo-600" />
                        </div>
                        <p className="text-sm text-gray-600">Click to upload image</p>
                        <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* OR divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">OR</span>
                </div>
              </div>

              {/* Image URL Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <input
                  type="url"
                  value={newInspirationData.imageUrl}
                  onChange={(e) => setNewInspirationData({ ...newInspirationData, imageUrl: e.target.value, imageFile: null })}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={addingInspiration || newInspirationData.imageFile !== null}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddInspirationModal(false);
                    setNewInspirationData({ imageUrl: '', productName: '', imageFile: null });
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={addingInspiration}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNewInspiration}
                  disabled={addingInspiration}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {addingInspiration ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <span>Add Inspiration</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateKitWizard; 